import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { authFetch } from '../services/authFetch';
import { useMsal } from '@azure/msal-react';
import { useGlobalFilters } from '../contexts/FilterContext';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  InputAdornment,
  Link,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import {
  ExpandMore,
  Dashboard,
  Apps,
  FilterList,
  Visibility,
  VisibilityOff,
  TrendingUp,
  Assessment,
  Security,
  CloudQueue,
  Speed,
  CheckCircle,
  Cancel,
  Warning,
  Help,
  Edit as EditIcon,
  Comment as CommentIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  OpenInNew,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  Info as InfoIcon,
  Search,
} from '@mui/icons-material';
import { transformApiDataToApplications, loadParameterLinksForApplication } from '../services/reliabilityService';
import { PARAMETER_LINKS, UPDATE_PARAMETER, API_BASE_URL, API_ENDPOINTS } from '../config/api';
import { fetchAuditHistory, AuditHistoryEntry } from '../services/auditHistoryService';

// Enhanced interfaces for enterprise data
interface ReliabilityMetric {
  name: string;
  parameterName?: string; // Add parameter_name for API calls
  completed: boolean;
  percentage?: number;
  status: 'completed' | 'partial' | 'not_started' | 'not_applicable';
  implementationStatus?: 'Yes' | 'No' | 'NA';
  completion_date?: string; // Completion date at parameter level
  definition?: string;
  implementationGuidance?: string;
  successCriteria?: string;
  remarks?: string;
  links?: Array<{ title: string; url: string; }>;
  excludeFromCalculation?: boolean; // Flag to exclude from progress calculation while keeping in UI
}

interface ApplicationData {
  appId: string;
  name: string;
  portfolio: string;
  platform: string;
  group: string;
  recoverySequence: string;
  type: string;
  sreContact: string;
  emSem: string;
  owners?: string;
  ownersRaw?: string;  // Owner emails for tooltip
  hosting: string;
  referenceId: string;
  crawlMetrics: Record<string, ReliabilityMetric>;
  walkMetrics: Record<string, ReliabilityMetric>;
  runMetrics: Record<string, ReliabilityMetric>;
  lastUpdated: string;
}

interface MetricAggregation {
  sum: number;
  count: number;
}

interface AggregatedMetric {
  percentage: number;
  implementationStatus: 'Yes' | 'No' | 'NA';
  yesCount: number; // Count of applications with 'Yes' status
  nonNACount: number; // Count of applications with non-NA status
}

interface PortfolioAggregation {
  portfolio: string;
  appCount: number;
  crawlProgress: Record<string, AggregatedMetric>;
  walkProgress: Record<string, AggregatedMetric>;
  runProgress: Record<string, AggregatedMetric>;
  crawlOverallPercentage: number;
  walkOverallPercentage: number;
  runOverallPercentage: number;
  applications: ApplicationData[];
  platformGroups: Record<string, {
    platform: string;
    applications: ApplicationData[];
    appCount: number;
    crawlProgress: Record<string, AggregatedMetric>;
    walkProgress: Record<string, AggregatedMetric>;
    runProgress: Record<string, AggregatedMetric>;
    crawlOverallPercentage: number;
    walkOverallPercentage: number;
    runOverallPercentage: number;
  }>;
}

const EnterpriseReliabilityFramework: React.FC = () => {
  // Azure AD Authentication
  const { accounts } = useMsal();

  // Use global filters from context
  const {
    selectedPortfolio: globalPortfolio,
    selectedPlatform: globalPlatform,
    setSelectedPortfolio: setGlobalPortfolio,
    setSelectedPlatform: setGlobalPlatform,
  } = useGlobalFilters();

  // Use global filters directly (they default to 'all')
  const selectedPortfolio = globalPortfolio;
  const selectedPlatform = globalPlatform;
  
  // Direct setters (no conversion needed)
  const setSelectedPortfolio = setGlobalPortfolio;
  const setSelectedPlatform = setGlobalPlatform;

  // Local multi-select state for platform filter (within Reliability Assessment tab only)
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(
    globalPlatform && globalPlatform !== 'all' ? [globalPlatform] : []
  );

  // View state
  const [viewMode, setViewMode] = useState<'portfolio' | 'application'>('portfolio');
  const [selectedApp, setSelectedApp] = useState<string>('A0423'); // Default to first app
  const [activePhaseTab, setActivePhaseTab] = useState<number>(0); // 0=Crawl, 1=Walk, 2=Run
  
  // Local filter states (not synced globally)
  const [selectedApplications, setSelectedApplications] = useState<string[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>('all');
  const [selectedSequence, setSelectedSequence] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');
  
  // App ID search state
  const [appIdSearch, setAppIdSearch] = useState<string>('');
  const [appIdSearchActive, setAppIdSearchActive] = useState<string>('');

  // Expanded sections
  const [expandedPortfolios, setExpandedPortfolios] = useState<string[]>([]);
  const [expandedPlatforms, setExpandedPlatforms] = useState<string[]>([]);
  
  // Application view phase expansion
  const [expandedPhases, setExpandedPhases] = useState<string[]>(['crawl', 'walk', 'run']); // All expanded by default
  
  // Filters panel collapse state
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  
  // Edit modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editingMetric, setEditingMetric] = useState<{
    appId: string;
    phase: 'crawl' | 'walk' | 'run';
    metricKey: string;
    metric: ReliabilityMetric;
  } | null>(null);
  const [editRemarks, setEditRemarks] = useState('');
  const [editLinks, setEditLinks] = useState<Array<{ title: string; url: string; }>>([]);
  const [editImplementationStatus, setEditImplementationStatus] = useState<'Yes' | 'No' | 'NA'>('No');
  const [editCompletionDate, setEditCompletionDate] = useState('');
  
  // Original values for audit tracking
  const [originalRemarks, setOriginalRemarks] = useState('');
  const [originalLinks, setOriginalLinks] = useState<Array<{ title: string; url: string; }>>([]);
  const [originalImplementationStatus, setOriginalImplementationStatus] = useState<'Yes' | 'No' | 'NA'>('No');
  const [originalCompletionDate, setOriginalCompletionDate] = useState('');
  
  const [applications, setApplications] = useState<ApplicationData[]>([]);
  
  // Links loading state
  const [loadedLinksForApps, setLoadedLinksForApps] = useState<Set<string>>(new Set());
  const [loadingLinks, setLoadingLinks] = useState(false);
  
  // Audit history state
  const [showAuditHistory, setShowAuditHistory] = useState(false);
  const [auditHistory, setAuditHistory] = useState<AuditHistoryEntry[]>([]);
  const [loadingAuditHistory, setLoadingAuditHistory] = useState(false);
  
  // API loading states
  const [loading, setLoading] = useState(true);
  const [dataReady, setDataReady] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  
  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');

  // Load parameter links for a specific application
  const loadLinksForSelectedApplication = useCallback(async (apps: ApplicationData[], selectedAppId: string) => {
    const selectedApp = apps.find(app => app.appId === selectedAppId);
    if (selectedApp && !loadedLinksForApps.has(selectedApp.referenceId)) {
      console.log(`🔗 Loading links for application: ${selectedApp.name} (apps loaded: ${apps.length})`);
      setLoadingLinks(true);
      
      try {
        // Import the fetch function directly
        const { fetchParameterLinks } = await import('../services/reliabilityService');
        
        // Fetch parameter links for this application
        const linksResponse = await fetchParameterLinks(selectedApp.referenceId);
        const parameterLinks = linksResponse.data.grouped_links || {};
        
        console.log(`📎 Fetched ${Object.keys(parameterLinks).length} parameter link groups`);
        
        // Update the applications array with new links
        setApplications(prevApps => {
          return prevApps.map(app => {
            if (app.appId === selectedAppId) {
              // Update crawl metrics with links
              const updatedCrawlMetrics = { ...app.crawlMetrics };
              Object.keys(updatedCrawlMetrics).forEach(metricKey => {
                const metric = updatedCrawlMetrics[metricKey];
                const paramLinks = parameterLinks[metric.parameterName || ''] || [];
                updatedCrawlMetrics[metricKey] = {
                  ...metric,
                  links: paramLinks.map((link: any) => ({
                    title: link.link_title,
                    url: link.link_url
                  }))
                };
              });
              
              // Update walk metrics with links
              const updatedWalkMetrics = { ...app.walkMetrics };
              Object.keys(updatedWalkMetrics).forEach(metricKey => {
                const metric = updatedWalkMetrics[metricKey];
                const paramLinks = parameterLinks[metric.parameterName || ''] || [];
                updatedWalkMetrics[metricKey] = {
                  ...metric,
                  links: paramLinks.map((link: any) => ({
                    title: link.link_title,
                    url: link.link_url
                  }))
                };
              });
              
              // Update run metrics with links
              const updatedRunMetrics = { ...app.runMetrics };
              Object.keys(updatedRunMetrics).forEach(metricKey => {
                const metric = updatedRunMetrics[metricKey];
                const paramLinks = parameterLinks[metric.parameterName || ''] || [];
                updatedRunMetrics[metricKey] = {
                  ...metric,
                  links: paramLinks.map((link: any) => ({
                    title: link.link_title,
                    url: link.link_url
                  }))
                };
              });
              
              return {
                ...app,
                crawlMetrics: updatedCrawlMetrics,
                walkMetrics: updatedWalkMetrics,
                runMetrics: updatedRunMetrics
              };
            }
            return app;
          });
        });
        
        // Mark this app as having loaded links
        setLoadedLinksForApps(prev => new Set(Array.from(prev).concat([selectedApp.referenceId])));
        
        console.log(`✅ Links loaded for ${selectedApp.name}`);
      } catch (error) {
        console.error('❌ Failed to load links:', error);
      } finally {
        setLoadingLinks(false);
      }
    }
  }, [loadedLinksForApps]);

  // Load applications data from API
  const loadApplicationsData = useCallback(async () => {
    // Reset states FIRST to prevent premature link loading during reload
    console.log('🔄 Resetting data states before new load...');
    setDataReady(false);
    setApplications([]); // Clear existing data to prevent stale state condition
    
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Loading applications data from API...');
      console.log('📊 About to call transformApiDataToApplications (portfolio-overview & parameters)...');
      
      // Fetch data from API
      const apiData = await transformApiDataToApplications();
      console.log('✅ API Data received:', Object.keys(apiData).length, 'applications');
      
      // Transform API data to match ApplicationData interface
      const transformedApplications: ApplicationData[] = Object.entries(apiData).map(([key, app]) => {
        // Convert reliability items to metrics format
        const transformMetrics = (items: Record<string, any>) => {
          const metrics: Record<string, ReliabilityMetric> = {};
          Object.entries(items).forEach(([itemName, item]) => {
            // Handle NA values separately - don't treat as completed
            const isCompleted = item.completed || item.implementationStatus === 'Yes';
            const isNA = item.implementationStatus === 'NA';
            const percentage = isNA ? 0 : (isCompleted ? 100 : 0);
            
            metrics[itemName] = {
              name: itemName,
              parameterName: item.parameterName, // Include parameterName for API calls
              completed: isCompleted,
              percentage: percentage,
              status: isNA ? 'not_applicable' : (isCompleted ? 'completed' : 'not_started'),
              implementationStatus: item.implementationStatus || 'No',
              definition: item.definition,
              implementationGuidance: item.implementationGuidance,
              successCriteria: item.successCriteria,
              remarks: item.remarks || '',
              links: item.links || [],
              excludeFromCalculation: item.excludeFromCalculation || false // Preserve exclusion flag
            };
          });
          return metrics;
        };

        return {
          appId: app.appId,
          name: app.name,
          portfolio: app.platformPortfolio,
          platform: app.platform,
          group: 'Core Services', // Default since not in API
          recoverySequence: 'Tier 1', // Default since not in API
          type: app.appType,
          sreContact: app.sreContact,
          emSem: app.emSem,
          owners: app.owners, // Owner names from API
          ownersRaw: app.ownersRaw, // Owner emails from API
          hosting: app.hosting,
          referenceId: app.referenceId,
          crawlMetrics: transformMetrics(app.crawl),
          walkMetrics: transformMetrics(app.walk),
          runMetrics: transformMetrics(app.run),
          lastUpdated: app.lastUpdated
        };
      });
      
      console.log('🎯 Transformed applications:', transformedApplications.length);
      setApplications(transformedApplications);
      setLastRefresh(new Date());
      
      // Set first app as selected if none is selected
      if (!selectedApp && transformedApplications.length > 0) {
        setSelectedApp(transformedApplications[0].appId);
        console.log('🎯 Selected first app:', transformedApplications[0].appId);
      }
      
      // Mark data as ready AFTER all state updates
      setDataReady(true);
      setInitialDataLoaded(true);
      console.log('🎯 Data is now ready for link loading');
      
      // Load parameter links immediately if in application view with specific app selected
      if (viewMode === 'application' && selectedApp) {
        await loadLinksForSelectedApplication(transformedApplications, selectedApp);
      }
    } catch (err) {
      console.error('❌ Error loading applications data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load applications data');
      setApplications([]);
      setDataReady(false);
    } finally {
      setLoading(false);
    }
  }, [selectedApp]);

  // Snackbar helper functions
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  // Audit history functions
  const loadAuditHistory = async () => {
    if (!editingMetric) return;
    
    setLoadingAuditHistory(true);
    try {
      const app = applications.find(a => a.appId === editingMetric.appId);
      if (app?.referenceId) {
        // Use the same parameter name logic as when creating audit entries
        const parameterName = editingMetric.metric.parameterName || editingMetric.metricKey;
        console.log('🔍 Fetching audit history for:', app.referenceId, 'parameter:', parameterName);
        
        const history = await fetchAuditHistory(app.referenceId, parameterName);
        console.log('📋 Audit history received:', history);
        
        // Get only the last 3 entries
        setAuditHistory(history.slice(0, 3));
      }
    } catch (error) {
      console.error('Error loading audit history:', error);
      showSnackbar('Failed to load audit history', 'error');
    } finally {
      setLoadingAuditHistory(false);
    }
  };

  const handleAuditHistoryClick = async () => {
    setShowAuditHistory(true);
    await loadAuditHistory();
  };

  // Load data on component mount (portfolio view) and when switching to application view
  useEffect(() => {
    if (viewMode === 'portfolio') {
      // Initial load for portfolio view
      loadApplicationsData();
    } else if (viewMode === 'application') {
      // Reload data when switching to application view to ensure fresh data
      console.log('🔄 Switching to application view, reloading data...');
      loadApplicationsData();
    }
  }, [viewMode, loadApplicationsData]);

  // Filter options derived from data with cascading filtering
  const portfolios = useMemo(() => 
    Array.from(new Set(applications.map(app => app.portfolio))).sort(),
    [applications]
  );
  
  // Platforms filtered by selected portfolio
  const platforms = useMemo(() => {
    const filteredApps = applications.filter(app => 
      selectedPortfolio === 'all' || app.portfolio === selectedPortfolio
    );
    return Array.from(new Set(filteredApps.map(app => app.platform))).sort();
  }, [applications, selectedPortfolio]);
  
  // Validate filters when data loads or changes
  useEffect(() => {
    if (applications.length > 0 && !loading) {
      // Validate portfolio
      if (selectedPortfolio !== 'all' && !portfolios.includes(selectedPortfolio)) {
        console.log('⚠️ Selected portfolio not found in Reliability data, resetting to all');
        setSelectedPortfolio('all');
      }
      
      // Validate platforms (multi-select)
      const invalidPlatforms = selectedPlatforms.filter(p => !platforms.includes(p));
      if (invalidPlatforms.length > 0) {
        console.log('⚠️ Some selected platforms not found in Reliability data, removing them');
        setSelectedPlatforms(prev => prev.filter(p => platforms.includes(p)));
      }
    }
  }, [applications, portfolios, platforms, loading]);
  
  // Applications filtered by selected portfolio and platform
  const availableApplications = useMemo(() => {
    return applications.filter(app => 
      (selectedPortfolio === 'all' || app.portfolio === selectedPortfolio) &&
      (selectedPlatforms.length === 0 || selectedPlatforms.includes(app.platform))
    );
  }, [applications, selectedPortfolio, selectedPlatforms]);
  
  // Groups filtered by selected portfolio and platform
  const groups = useMemo(() => {
    const filteredApps = applications.filter(app => 
      (selectedPortfolio === 'all' || app.portfolio === selectedPortfolio) &&
      (selectedPlatforms.length === 0 || selectedPlatforms.includes(app.platform))
    );
    return Array.from(new Set(filteredApps.map(app => app.group))).sort();
  }, [applications, selectedPortfolio, selectedPlatforms]);

  // Reset dependent filters when parent filters change and selected values become invalid
  // Note: Platform is preserved when navigating between tabs via global filter context

  useEffect(() => {
    // Don't reset view mode while loading data (applications array is temporarily empty)
    if (!loading && selectedApplications.length > 0) {
      const validApps = selectedApplications.filter(appId => availableApplications.some(app => app.appId === appId));
      if (validApps.length !== selectedApplications.length) {
        setSelectedApplications(validApps);
        if (viewMode === 'application' && validApps.length === 0) {
          setViewMode('portfolio');
        }
      }
    }
  }, [availableApplications, selectedApplications, viewMode, loading]);

  useEffect(() => {
    if (selectedGroup !== 'all' && !groups.includes(selectedGroup)) {
      setSelectedGroup('all');
    }
  }, [groups, selectedGroup]);

  // Filtered applications
  const filteredApplications = useMemo(() => {
    return applications.filter(app => {
      const matchesPortfolio = selectedPortfolio === 'all' || app.portfolio === selectedPortfolio;
      const matchesPlatform = selectedPlatforms.length === 0 || selectedPlatforms.includes(app.platform);
      const matchesApplication = selectedApplications.length === 0 || selectedApplications.includes(app.appId);
      const matchesGroup = selectedGroup === 'all' || app.group === selectedGroup;
      const matchesSearch = !appIdSearchActive || app.appId.toLowerCase().includes(appIdSearchActive.toLowerCase()) || app.name.toLowerCase().includes(appIdSearchActive.toLowerCase());
      return matchesPortfolio && matchesPlatform && matchesApplication && matchesGroup && matchesSearch;
    });
  }, [applications, selectedPortfolio, selectedPlatforms, selectedApplications, selectedGroup, appIdSearchActive]);

  // Portfolio aggregations
  const portfolioAggregations = useMemo(() => {
    const aggregations = new Map<string, PortfolioAggregation>();
    
    filteredApplications.forEach(app => {
      if (!aggregations.has(app.portfolio)) {
        aggregations.set(app.portfolio, {
          portfolio: app.portfolio,
          appCount: 0,
          crawlProgress: {},
          walkProgress: {},
          runProgress: {},
          crawlOverallPercentage: 0,
          walkOverallPercentage: 0,
          runOverallPercentage: 0,
          applications: [],
          platformGroups: {},
        });
      }
      
      const agg = aggregations.get(app.portfolio)!;
      agg.appCount++;
      agg.applications.push(app);
      
      // Group applications by platform within portfolio
      if (!agg.platformGroups[app.platform]) {
        agg.platformGroups[app.platform] = {
          platform: app.platform,
          applications: [],
          appCount: 0,
          crawlProgress: {},
          walkProgress: {},
          runProgress: {},
          crawlOverallPercentage: 0,
          walkOverallPercentage: 0,
          runOverallPercentage: 0,
        };
      }
      agg.platformGroups[app.platform].applications.push(app);
      agg.platformGroups[app.platform].appCount++;
    });

    // Calculate metric aggregations (exclude NA values) - separate loop for cleaner logic
    filteredApplications.forEach(app => {
      const agg = aggregations.get(app.portfolio)!;
      const platformGroup = agg.platformGroups[app.platform];
      
      // Calculate metric aggregations for portfolio (exclude NA values)
      Object.keys(app.crawlMetrics).forEach(metric => {
        if (!agg.crawlProgress[metric]) {
          agg.crawlProgress[metric] = { percentage: 0, implementationStatus: 'No', yesCount: 0, nonNACount: 0 };
        }
        // Only include non-NA values in aggregation
        if (app.crawlMetrics[metric].implementationStatus !== 'NA') {
          agg.crawlProgress[metric].percentage += app.crawlMetrics[metric].percentage || 0;
        }
      });
      
      Object.keys(app.walkMetrics).forEach(metric => {
        if (!agg.walkProgress[metric]) {
          agg.walkProgress[metric] = { percentage: 0, implementationStatus: 'No', yesCount: 0, nonNACount: 0 };
        }
        // Only include non-NA values in aggregation
        if (app.walkMetrics[metric].implementationStatus !== 'NA') {
          agg.walkProgress[metric].percentage += app.walkMetrics[metric].percentage || 0;
        }
      });
      
      Object.keys(app.runMetrics).forEach(metric => {
        if (!agg.runProgress[metric]) {
          agg.runProgress[metric] = { percentage: 0, implementationStatus: 'No', yesCount: 0, nonNACount: 0 };
        }
        // Only include non-NA values in aggregation
        if (app.runMetrics[metric].implementationStatus !== 'NA') {
          agg.runProgress[metric].percentage += app.runMetrics[metric].percentage || 0;
        }
      });

      // Calculate metric aggregations for platform (exclude NA values)
      Object.keys(app.crawlMetrics).forEach(metric => {
        if (!platformGroup.crawlProgress[metric]) {
          platformGroup.crawlProgress[metric] = { percentage: 0, implementationStatus: 'No', yesCount: 0, nonNACount: 0 };
        }
        // Only include non-NA values in aggregation
        if (app.crawlMetrics[metric].implementationStatus !== 'NA') {
          platformGroup.crawlProgress[metric].percentage += app.crawlMetrics[metric].percentage || 0;
        }
      });
      
      Object.keys(app.walkMetrics).forEach(metric => {
        if (!platformGroup.walkProgress[metric]) {
          platformGroup.walkProgress[metric] = { percentage: 0, implementationStatus: 'No', yesCount: 0, nonNACount: 0 };
        }
        // Only include non-NA values in aggregation
        if (app.walkMetrics[metric].implementationStatus !== 'NA') {
          platformGroup.walkProgress[metric].percentage += app.walkMetrics[metric].percentage || 0;
        }
      });
      
      Object.keys(app.runMetrics).forEach(metric => {
        if (!platformGroup.runProgress[metric]) {
          platformGroup.runProgress[metric] = { percentage: 0, implementationStatus: 'No', yesCount: 0, nonNACount: 0 };
        }
        // Only include non-NA values in aggregation
        if (app.runMetrics[metric].implementationStatus !== 'NA') {
          platformGroup.runProgress[metric].percentage += app.runMetrics[metric].percentage || 0;
        }
      });
    });
    
    // Calculate averages and overall percentages
    aggregations.forEach(agg => {
      // Portfolio-level averages (using count of non-NA applications)
      Object.keys(agg.crawlProgress).forEach(metric => {
        // Count applications that have non-NA values for this metric
        const nonNAApps = agg.applications.filter(app => 
          app.crawlMetrics[metric] && app.crawlMetrics[metric].implementationStatus !== 'NA'
        );
        const yesApps = agg.applications.filter(app =>
          app.crawlMetrics[metric] && app.crawlMetrics[metric].implementationStatus === 'Yes'
        );
        const allNAApps = agg.applications.filter(app => 
          app.crawlMetrics[metric] && app.crawlMetrics[metric].implementationStatus === 'NA'
        );
        
        agg.crawlProgress[metric].nonNACount = nonNAApps.length;
        agg.crawlProgress[metric].yesCount = yesApps.length;
        
        if (nonNAApps.length > 0) {
          agg.crawlProgress[metric].percentage = parseFloat((agg.crawlProgress[metric].percentage / nonNAApps.length).toFixed(2));
          agg.crawlProgress[metric].implementationStatus = 'No'; // Will be updated based on percentage
        } else if (allNAApps.length > 0) {
          // All applications have NA for this metric
          agg.crawlProgress[metric].percentage = 0;
          agg.crawlProgress[metric].implementationStatus = 'NA';
        } else {
          agg.crawlProgress[metric].percentage = 0;
          agg.crawlProgress[metric].implementationStatus = 'No';
        }
      });
      Object.keys(agg.walkProgress).forEach(metric => {
        // Count applications that have non-NA values for this metric
        const nonNAApps = agg.applications.filter(app => 
          app.walkMetrics[metric] && app.walkMetrics[metric].implementationStatus !== 'NA'
        );
        const yesApps = agg.applications.filter(app =>
          app.walkMetrics[metric] && app.walkMetrics[metric].implementationStatus === 'Yes'
        );
        const allNAApps = agg.applications.filter(app => 
          app.walkMetrics[metric] && app.walkMetrics[metric].implementationStatus === 'NA'
        );
        
        agg.walkProgress[metric].nonNACount = nonNAApps.length;
        agg.walkProgress[metric].yesCount = yesApps.length;
        
        if (nonNAApps.length > 0) {
          agg.walkProgress[metric].percentage = parseFloat((agg.walkProgress[metric].percentage / nonNAApps.length).toFixed(2));
          agg.walkProgress[metric].implementationStatus = 'No'; // Will be updated based on percentage
        } else if (allNAApps.length > 0) {
          // All applications have NA for this metric
          agg.walkProgress[metric].percentage = 0;
          agg.walkProgress[metric].implementationStatus = 'NA';
        } else {
          agg.walkProgress[metric].percentage = 0;
          agg.walkProgress[metric].implementationStatus = 'No';
        }
      });
      Object.keys(agg.runProgress).forEach(metric => {
        // Count applications that have non-NA values for this metric
        const nonNAApps = agg.applications.filter(app => 
          app.runMetrics[metric] && app.runMetrics[metric].implementationStatus !== 'NA'
        );
        const yesApps = agg.applications.filter(app =>
          app.runMetrics[metric] && app.runMetrics[metric].implementationStatus === 'Yes'
        );
        const allNAApps = agg.applications.filter(app => 
          app.runMetrics[metric] && app.runMetrics[metric].implementationStatus === 'NA'
        );
        
        agg.runProgress[metric].nonNACount = nonNAApps.length;
        agg.runProgress[metric].yesCount = yesApps.length;
        
        if (nonNAApps.length > 0) {
          agg.runProgress[metric].percentage = parseFloat((agg.runProgress[metric].percentage / nonNAApps.length).toFixed(2));
          agg.runProgress[metric].implementationStatus = 'No'; // Will be updated based on percentage
        } else if (allNAApps.length > 0) {
          // All applications have NA for this metric
          agg.runProgress[metric].percentage = 0;
          agg.runProgress[metric].implementationStatus = 'NA';
        } else {
          agg.runProgress[metric].percentage = 0;
          agg.runProgress[metric].implementationStatus = 'No';
        }
      });
      
      // Portfolio overall percentages (exclude NA values from calculation)
      // Calculate based on sum of counts instead of average of percentages
      const crawlTotalYes = Object.values(agg.crawlProgress)
        .filter(val => val.implementationStatus !== 'NA')
        .reduce((sum, val) => sum + val.yesCount, 0);
      const crawlTotalNonNA = Object.values(agg.crawlProgress)
        .filter(val => val.implementationStatus !== 'NA')
        .reduce((sum, val) => sum + val.nonNACount, 0);
      
      agg.crawlOverallPercentage = crawlTotalNonNA > 0 
        ? parseFloat(((crawlTotalYes / crawlTotalNonNA) * 100).toFixed(2))
        : 0;
      
      // For Walk phase, exclude metrics that are flagged for exclusion from overall calculation
      const walkTotalYes = Object.entries(agg.walkProgress)
        .filter(([metricKey, value]) => {
          const sampleApp = agg.applications[0];
          return sampleApp && sampleApp.walkMetrics[metricKey] && 
                 !sampleApp.walkMetrics[metricKey].excludeFromCalculation &&
                 value.implementationStatus !== 'NA';
        })
        .reduce((sum, [key, value]) => sum + value.yesCount, 0);
      
      const walkTotalNonNA = Object.entries(agg.walkProgress)
        .filter(([metricKey, value]) => {
          const sampleApp = agg.applications[0];
          return sampleApp && sampleApp.walkMetrics[metricKey] && 
                 !sampleApp.walkMetrics[metricKey].excludeFromCalculation &&
                 value.implementationStatus !== 'NA';
        })
        .reduce((sum, [key, value]) => sum + value.nonNACount, 0);
      
      agg.walkOverallPercentage = walkTotalNonNA > 0
        ? parseFloat(((walkTotalYes / walkTotalNonNA) * 100).toFixed(2))
        : 0;
      
      const runTotalYes = Object.values(agg.runProgress)
        .filter(val => val.implementationStatus !== 'NA')
        .reduce((sum, val) => sum + val.yesCount, 0);
      const runTotalNonNA = Object.values(agg.runProgress)
        .filter(val => val.implementationStatus !== 'NA')
        .reduce((sum, val) => sum + val.nonNACount, 0);
        
      agg.runOverallPercentage = runTotalNonNA > 0
        ? parseFloat(((runTotalYes / runTotalNonNA) * 100).toFixed(2))
        : 0;

      // Platform-level averages and overall percentages
      Object.values(agg.platformGroups).forEach(platformGroup => {
        // Platform averages (using count of non-NA applications)
        Object.keys(platformGroup.crawlProgress).forEach(metric => {
          // Count applications that have non-NA values for this metric
          const nonNAApps = platformGroup.applications.filter(app => 
            app.crawlMetrics[metric] && app.crawlMetrics[metric].implementationStatus !== 'NA'
          );
          const yesApps = platformGroup.applications.filter(app =>
            app.crawlMetrics[metric] && app.crawlMetrics[metric].implementationStatus === 'Yes'
          );
          const allNAApps = platformGroup.applications.filter(app => 
            app.crawlMetrics[metric] && app.crawlMetrics[metric].implementationStatus === 'NA'
          );
          
          platformGroup.crawlProgress[metric].nonNACount = nonNAApps.length;
          platformGroup.crawlProgress[metric].yesCount = yesApps.length;
          
          if (nonNAApps.length > 0) {
            platformGroup.crawlProgress[metric].percentage = parseFloat((platformGroup.crawlProgress[metric].percentage / nonNAApps.length).toFixed(2));
            platformGroup.crawlProgress[metric].implementationStatus = 'No'; // Will be updated based on percentage
          } else if (allNAApps.length > 0) {
            // All applications have NA for this metric
            platformGroup.crawlProgress[metric].percentage = 0;
            platformGroup.crawlProgress[metric].implementationStatus = 'NA';
          } else {
            platformGroup.crawlProgress[metric].percentage = 0;
            platformGroup.crawlProgress[metric].implementationStatus = 'No';
          }
        });
        Object.keys(platformGroup.walkProgress).forEach(metric => {
          // Count applications that have non-NA values for this metric
          const nonNAApps = platformGroup.applications.filter(app => 
            app.walkMetrics[metric] && app.walkMetrics[metric].implementationStatus !== 'NA'
          );
          const yesApps = platformGroup.applications.filter(app =>
            app.walkMetrics[metric] && app.walkMetrics[metric].implementationStatus === 'Yes'
          );
          const allNAApps = platformGroup.applications.filter(app => 
            app.walkMetrics[metric] && app.walkMetrics[metric].implementationStatus === 'NA'
          );
          
          platformGroup.walkProgress[metric].nonNACount = nonNAApps.length;
          platformGroup.walkProgress[metric].yesCount = yesApps.length;
          
          if (nonNAApps.length > 0) {
            platformGroup.walkProgress[metric].percentage = parseFloat((platformGroup.walkProgress[metric].percentage / nonNAApps.length).toFixed(2));
            platformGroup.walkProgress[metric].implementationStatus = 'No'; // Will be updated based on percentage
          } else if (allNAApps.length > 0) {
            // All applications have NA for this metric
            platformGroup.walkProgress[metric].percentage = 0;
            platformGroup.walkProgress[metric].implementationStatus = 'NA';
          } else {
            platformGroup.walkProgress[metric].percentage = 0;
            platformGroup.walkProgress[metric].implementationStatus = 'No';
          }
        });
        Object.keys(platformGroup.runProgress).forEach(metric => {
          // Count applications that have non-NA values for this metric
          const nonNAApps = platformGroup.applications.filter(app => 
            app.runMetrics[metric] && app.runMetrics[metric].implementationStatus !== 'NA'
          );
          const yesApps = platformGroup.applications.filter(app =>
            app.runMetrics[metric] && app.runMetrics[metric].implementationStatus === 'Yes'
          );
          const allNAApps = platformGroup.applications.filter(app => 
            app.runMetrics[metric] && app.runMetrics[metric].implementationStatus === 'NA'
          );
          
          platformGroup.runProgress[metric].nonNACount = nonNAApps.length;
          platformGroup.runProgress[metric].yesCount = yesApps.length;
          
          if (nonNAApps.length > 0) {
            platformGroup.runProgress[metric].percentage = parseFloat((platformGroup.runProgress[metric].percentage / nonNAApps.length).toFixed(2));
            platformGroup.runProgress[metric].implementationStatus = 'No'; // Will be updated based on percentage
          } else if (allNAApps.length > 0) {
            // All applications have NA for this metric
            platformGroup.runProgress[metric].percentage = 0;
            platformGroup.runProgress[metric].implementationStatus = 'NA';
          } else {
            platformGroup.runProgress[metric].percentage = 0;
            platformGroup.runProgress[metric].implementationStatus = 'No';
          }
        });

        // Platform overall percentages (exclude NA values from calculation)
        // Calculate based on sum of counts instead of average of percentages
        const platformCrawlTotalYes = Object.values(platformGroup.crawlProgress)
          .filter(val => val.implementationStatus !== 'NA')
          .reduce((sum, val) => sum + val.yesCount, 0);
        const platformCrawlTotalNonNA = Object.values(platformGroup.crawlProgress)
          .filter(val => val.implementationStatus !== 'NA')
          .reduce((sum, val) => sum + val.nonNACount, 0);
        
        platformGroup.crawlOverallPercentage = platformCrawlTotalNonNA > 0 
          ? parseFloat(((platformCrawlTotalYes / platformCrawlTotalNonNA) * 100).toFixed(2))
          : 0;
        
        // For Walk phase, exclude metrics that are flagged for exclusion from overall calculation
        const platformWalkTotalYes = Object.entries(platformGroup.walkProgress)
          .filter(([metricKey, value]) => {
            const sampleApp = platformGroup.applications[0];
            return sampleApp && sampleApp.walkMetrics[metricKey] && 
                   !sampleApp.walkMetrics[metricKey].excludeFromCalculation &&
                   value.implementationStatus !== 'NA';
          })
          .reduce((sum, [key, value]) => sum + value.yesCount, 0);
        
        const platformWalkTotalNonNA = Object.entries(platformGroup.walkProgress)
          .filter(([metricKey, value]) => {
            const sampleApp = platformGroup.applications[0];
            return sampleApp && sampleApp.walkMetrics[metricKey] && 
                   !sampleApp.walkMetrics[metricKey].excludeFromCalculation &&
                   value.implementationStatus !== 'NA';
          })
          .reduce((sum, [key, value]) => sum + value.nonNACount, 0);
        
        platformGroup.walkOverallPercentage = platformWalkTotalNonNA > 0
          ? parseFloat(((platformWalkTotalYes / platformWalkTotalNonNA) * 100).toFixed(2))
          : 0;
        
        const platformRunTotalYes = Object.values(platformGroup.runProgress)
          .filter(val => val.implementationStatus !== 'NA')
          .reduce((sum, val) => sum + val.yesCount, 0);
        const platformRunTotalNonNA = Object.values(platformGroup.runProgress)
          .filter(val => val.implementationStatus !== 'NA')
          .reduce((sum, val) => sum + val.nonNACount, 0);
          
        platformGroup.runOverallPercentage = platformRunTotalNonNA > 0
          ? parseFloat(((platformRunTotalYes / platformRunTotalNonNA) * 100).toFixed(2))
          : 0;
      });
    });
    
    return Array.from(aggregations.values()).sort((a, b) => a.portfolio.localeCompare(b.portfolio));
  }, [filteredApplications]);

  // Status color helper for calculations (excludes NA from calculations)
  const getStatusColor = (percentage: number, implementationStatus?: string) => {
    // Show NA as grey and exclude from calculations
    if (implementationStatus === 'NA') return '#6c757d';  // Grey for NA
    if (percentage >= 80) return '#27ae60';  // Green
    if (percentage >= 50) return '#f39c12';  // Orange  
    if (percentage > 0) return '#e74c3c';    // Red
    return '#e74c3c';                        // Red for 0% (No value)
  };

  // Status color helper for display (shows NA as grey)
  const getDisplayColor = (percentage: number, implementationStatus?: string) => {
    if (implementationStatus === 'NA') return '#6c757d';  // Gray for NA display
    if (percentage >= 80) return '#27ae60';  // Green
    if (percentage >= 50) return '#f39c12';  // Orange  
    if (percentage > 0) return '#e74c3c';    // Red
    return '#e74c3c';                        // Red for 0% (No value)
  };

  // Status icon helper
  const getStatusIcon = (percentage: number, implementationStatus?: string) => {
    // Show different icon for NA values
    if (implementationStatus === 'NA') return <Help sx={{ color: '#6c757d', fontSize: 16 }} />;
    if (percentage >= 80) return <CheckCircle sx={{ color: '#27ae60', fontSize: 16 }} />;
    if (percentage >= 50) return <Warning sx={{ color: '#f39c12', fontSize: 16 }} />;
    if (percentage > 0) return <Cancel sx={{ color: '#e74c3c', fontSize: 16 }} />;
    return <Cancel sx={{ color: '#e74c3c', fontSize: 16 }} />;  // Red cross for 0% (No value)
  };

  // Status text helper - shows "NA" for NA values instead of percentage
  const getStatusText = (percentage: number, implementationStatus?: string) => {
    if (implementationStatus === 'NA') return 'NA';
    return `${percentage}%`;
  };

  const handlePortfolioExpand = (portfolio: string) => {
    setExpandedPortfolios(prev => 
      prev.includes(portfolio) 
        ? prev.filter(p => p !== portfolio)
        : [...prev, portfolio]
    );
  };

  // Calculate stage percentage
  const calculateStagePercentage = (metrics: Record<string, ReliabilityMetric>) => {
    // Filter out metrics that are excluded from calculation
    const filteredMetrics = Object.values(metrics).filter(metric => !metric.excludeFromCalculation);
    
    if (filteredMetrics.length === 0) return 0;
    
    // Count only applicable metrics (exclude NA from calculations)
    const applicableMetrics = filteredMetrics.filter(metric => metric.implementationStatus !== 'NA');
    const totalPercentage = applicableMetrics.reduce((sum, metric) => {
      // Only "Yes" should contribute 100% to the calculation
      const effectivePercentage = metric.implementationStatus === 'Yes' ? 100 : 0;
      return sum + effectivePercentage;
    }, 0);
    
    // Use only the applicable metrics in the calculation - return with 2 decimal places
    return applicableMetrics.length > 0 ? parseFloat((totalPercentage / applicableMetrics.length).toFixed(2)) : 0;
  };

  // Toggle phase expansion
  const togglePhaseExpansion = (phase: string) => {
    setExpandedPhases(prev => 
      prev.includes(phase) 
        ? prev.filter(p => p !== phase)
        : [...prev, phase]
    );
  };

  // Global collapse/expand all phases
  const collapseAllPhases = () => {
    setExpandedPhases([]);
  };

  const expandAllPhases = () => {
    setExpandedPhases(['crawl', 'walk', 'run']);
  };

  // Toggle filters panel
  const toggleFiltersPanel = () => {
    setFiltersCollapsed(prev => !prev);
  };

  const handlePlatformExpand = (platformKey: string) => {
    setExpandedPlatforms(prev => 
      prev.includes(platformKey) 
        ? prev.filter(p => p !== platformKey)
        : [...prev, platformKey]
    );
  };

  // App ID search handler
  const handleAppIdSearch = () => {
    const searchTerm = appIdSearch.trim();
    if (!searchTerm) return;
    
    setAppIdSearchActive(searchTerm);
    
    // Find matching application
    const matchingApp = applications.find(app => 
      app.appId.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    if (matchingApp) {
      // Switch to application view
      setViewMode('application');
      setSelectedApp(matchingApp.appId);
      setSelectedApplications([matchingApp.appId]);
      
      // Also set portfolio and platform filters to match the app
      setSelectedPortfolio(matchingApp.portfolio);
      setSelectedPlatforms([matchingApp.platform]);
    }
  };

  const handleClearAppIdSearch = () => {
    setAppIdSearch('');
    setAppIdSearchActive('');
  };

  // Edit functions
  const openEditModal = async (appId: string, phase: 'crawl' | 'walk' | 'run', metricKey: string, metric: ReliabilityMetric) => {
    console.log('🔍 Opening edit modal for:', { appId, phase, metricKey, implementationStatus: metric.implementationStatus });
    
    // Find the application to get the reference ID
    const app = applications.find(a => a.appId === appId);
    if (!app) {
      console.error('❌ Application not found:', appId);
      return;
    }



    // Fetch the latest parameter status directly
    try {
      console.log('🔄 Fetching parameter status for edit modal...');
      const response = await authFetch(PARAMETER_LINKS(app.referenceId));
      
      if (response.ok) {
        const data = await response.json();
        const parameterStatus = data.data?.parameter_status || {};
        const paramStatus = parameterStatus[metric.parameterName || metricKey];
        
        console.log('📋 Parameter status from API:', {
          parameterName: metric.parameterName || metricKey,
          paramStatus,
          fullParameterStatus: parameterStatus
        });

        // Use API data if available, otherwise fall back to current metric data
        const finalRemarks = paramStatus?.remark || metric.remarks || '';
        const finalLinks = metric.links || [];
        const finalStatus = metric.implementationStatus || 'No';
        const finalCompletionDate = paramStatus?.completion_date || metric.completion_date || '';
        
        setEditingMetric({ appId, phase, metricKey, metric });
        setEditRemarks(finalRemarks);
        setEditLinks(finalLinks);
        setEditImplementationStatus(finalStatus);
        setEditCompletionDate(finalCompletionDate);
        
        // Store original values for audit tracking
        setOriginalRemarks(finalRemarks);
        setOriginalLinks(finalLinks);
        setOriginalImplementationStatus(finalStatus);
        setOriginalCompletionDate(finalCompletionDate);
      } else {
        // Fallback to current metric data
        console.log('⚠️ Could not fetch parameter status, using current data');
        const finalRemarks = metric.remarks || '';
        const finalLinks = metric.links || [];
        const finalStatus = metric.implementationStatus || 'No';
        const finalCompletionDate = metric.completion_date || '';
        
        setEditingMetric({ appId, phase, metricKey, metric });
        setEditRemarks(finalRemarks);
        setEditLinks(finalLinks);
        setEditImplementationStatus(finalStatus);
        setEditCompletionDate(finalCompletionDate);
        
        // Store original values for audit tracking
        setOriginalRemarks(finalRemarks);
        setOriginalLinks(finalLinks);
        setOriginalImplementationStatus(finalStatus);
        setOriginalCompletionDate(finalCompletionDate);
      }
    } catch (error) {
      console.error('❌ Error fetching parameter status:', error);
      // Fallback to current metric data
      const finalRemarks = metric.remarks || '';
      const finalLinks = metric.links || [];
      const finalStatus = metric.implementationStatus || 'No';
      const finalCompletionDate = metric.completion_date || '';
      
      setEditingMetric({ appId, phase, metricKey, metric });
      setEditRemarks(finalRemarks);
      setEditLinks(finalLinks);
      setEditImplementationStatus(finalStatus);
      setEditCompletionDate(finalCompletionDate);
      
      // Store original values for audit tracking
      setOriginalRemarks(finalRemarks);
      setOriginalLinks(finalLinks);
      setOriginalImplementationStatus(finalStatus);
      setOriginalCompletionDate(finalCompletionDate);
    }
    
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingMetric(null);
    setEditRemarks('');
    setEditLinks([]);
    setEditImplementationStatus('No');
    setEditCompletionDate('');
    setEditSaving(false);
    
    // Clear original values
    setOriginalRemarks('');
    setOriginalLinks([]);
    setOriginalImplementationStatus('No');
    setOriginalCompletionDate('');
  };

  const saveMetricEdit = async () => {
    if (!editingMetric) return;

    // Validation: If status is NA, remarks are mandatory
    if (editImplementationStatus === 'NA' && !editRemarks.trim()) {
      showSnackbar('Remarks are mandatory when Implementation Status is "NA"', 'warning');
      return;
    }

    setEditSaving(true);

    try {
      // Prepare API payload
      const app = applications.find(a => a.appId === editingMetric.appId);
      if (!app) return;

      const apiPayload = {
        reference_id: app.referenceId,
        parameter_name: editingMetric.metric.parameterName || editingMetric.metricKey, // Use parameterName if available
        parameter_value: editImplementationStatus,
        remark: editRemarks.trim() || `Updated ${editingMetric.metric.name}`,
        completion_date: editCompletionDate || new Date().toISOString().split('T')[0],
        links: editLinks
          .filter(link => link.title.trim() && link.url.trim())
          .map(link => ({
            link_title: link.title.trim(),
            link_url: link.url.trim(),
            link_type: "documentation", // Default type
            link_description: `Link for ${editingMetric.metric.name}`,
            is_primary: false,
            is_active: true
          }))
      };

      console.log('📤 Saving metric data:', apiPayload);

      // Call the API
      const response = await authFetch(UPDATE_PARAMETER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiPayload)
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorData}`);
      }

      const result = await response.json();
      console.log('✅ API Response:', result);

      // Create comprehensive audit entries for all changed fields
      try {
        // Get current user information
        const getCurrentUserInfo = () => {
          try {
            // First priority: Azure AD authenticated user
            if (accounts && accounts.length > 0) {
              const azureUser = accounts[0];
              return {
                name: azureUser.name || azureUser.username || 'Azure User',
                email: azureUser.username || ''
              };
            }

            // Fallback: Try to get from localStorage (test user)
            const storedUser = localStorage.getItem('maersk_user');
            if (storedUser) {
              const user = JSON.parse(storedUser);
              return {
                name: user.name || 'Unknown User',
                email: user.username || user.email || ''
              };
            }
            
            // Last fallback
            return {
              name: 'Current User',
              email: ''
            };
          } catch (error) {
            console.warn('Could not get user info:', error);
            return {
              name: 'System User',
              email: ''
            };
          }
        };

        const userInfo = getCurrentUserInfo();
        const parameterName = editingMetric.metric.parameterName || editingMetric.metricKey;
        
        // Track all changes in a single consolidated entry
        const changes = [];
        const changedFields = [];

        // 1. Track Implementation Status changes
        if (originalImplementationStatus !== editImplementationStatus) {
          changes.push(`Implementation Status: '${originalImplementationStatus}' → '${editImplementationStatus}'`);
          changedFields.push('Implementation Status');
        }

        // 2. Track Remarks changes
        if (originalRemarks !== editRemarks) {
          changes.push(`Remarks: '${originalRemarks || 'No remarks'}' → '${editRemarks || 'No remarks'}'`);
          changedFields.push('Remarks');
        }

        // 3. Track Completion Date changes
        if (originalCompletionDate !== editCompletionDate) {
          changes.push(`Completion Date: '${originalCompletionDate || 'Not set'}' → '${editCompletionDate || 'Not set'}'`);
          changedFields.push('Completion Date');
        }

        // 4. Track Links changes - Show actual link details
        const originalValidLinks = originalLinks.filter(link => link.title.trim() && link.url.trim());
        const newValidLinks = editLinks.filter(link => link.title.trim() && link.url.trim());
        const originalLinksStr = JSON.stringify(originalValidLinks);
        const newLinksStr = JSON.stringify(newValidLinks);
        
        if (originalLinksStr !== newLinksStr) {
          const formatLinks = (links: Array<{ title: string; url: string }>) => {
            if (links.length === 0) return 'No links';
            return links.map(link => `"${link.title}" (${link.url})`).join(', ');
          };
          
          const originalLinksDisplay = formatLinks(originalValidLinks);
          const newLinksDisplay = formatLinks(newValidLinks);
          changes.push(`Links: [${originalLinksDisplay}] → [${newLinksDisplay}]`);
          changedFields.push('Links');
        }

        // Create a single audit entry if there are any changes
        if (changes.length > 0) {
          const fieldChangedText = changedFields.join(', ');
          const auditData = {
            reference_id: app.referenceId,
            application_name: app.name,
            section: app.portfolio,
            parameter_name: parameterName,
            field_changed: fieldChangedText, // Show actual changed field names
            old_value: originalImplementationStatus, // Use stored original value
            new_value: editImplementationStatus,
            changed_by: userInfo.name,
            user_email: userInfo.email,
            change_description: changes.join('; ')
          };

          console.log('📝 Creating consolidated audit entry:', auditData);
          
          try {
            const auditResponse = await authFetch(API_ENDPOINTS.CREATE_AUDIT_ENTRY, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(auditData)
            });

            if (auditResponse.ok) {
              console.log('✅ Consolidated audit entry created successfully');
            } else {
              console.warn('⚠️ Failed to create audit entry:', await auditResponse.text());
            }
          } catch (entryError) {
            console.warn('⚠️ Error creating audit entry:', entryError);
          }
        } else {
          console.log('ℹ️ No changes detected, skipping audit entry');
        }

      } catch (auditError) {
        console.warn('⚠️ Error processing audit entries:', auditError);
      }

      // Update local state after successful API call
      const updatedApps = [...applications];
      const appIndex = updatedApps.findIndex(app => app.appId === editingMetric.appId);
      if (appIndex === -1) return;

      const phaseKey = editingMetric.phase + 'Metrics' as keyof ApplicationData;
      const updatedMetrics = { ...updatedApps[appIndex][phaseKey] as Record<string, ReliabilityMetric> };
      
      // Update percentage based on implementation status
      let newPercentage = 0;
      let newCompleted = false;
      let newStatus: 'completed' | 'partial' | 'not_started' | 'not_applicable' = 'not_started';
      
      switch (editImplementationStatus) {
        case 'Yes':
          newPercentage = 100;
          newCompleted = true;
          newStatus = 'completed';
          break;
        case 'No':
          newPercentage = 0;
          newCompleted = false;
          newStatus = 'not_started';
          break;
        case 'NA':
          newPercentage = 0; // NA should not count in calculations
          newCompleted = false; // Don't treat NA as completed
          newStatus = 'not_applicable'; // Keep as not_applicable
          break;
      }
      
      updatedMetrics[editingMetric.metricKey] = {
        ...editingMetric.metric,
        implementationStatus: editImplementationStatus,
        percentage: newPercentage,
        completed: newCompleted,
        status: newStatus,
        remarks: editRemarks,
        completion_date: editCompletionDate,
        links: editLinks.filter(link => link.title.trim() || link.url.trim()),
      };

      console.log('💾 Updating local state for metric:', {
        metricKey: editingMetric.metricKey,
        oldStatus: editingMetric.metric.implementationStatus,
        newStatus: editImplementationStatus,
        updatedMetric: updatedMetrics[editingMetric.metricKey]
      });

      // Create a completely new app object to ensure React detects the change
      updatedApps[appIndex] = {
        ...updatedApps[appIndex],
        [phaseKey]: updatedMetrics
      };
      
      setApplications(updatedApps);
      
      console.log('✅ Local state updated successfully');
      
      // Show success message
      showSnackbar('Metric updated successfully!', 'success');
      closeEditModal();

    } catch (error) {
      console.error('❌ Error saving metric:', error);
      showSnackbar(`Failed to save metric: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    } finally {
      setEditSaving(false);
    }
  };

  const addLink = () => {
    setEditLinks([...editLinks, { 
      title: '', 
      url: ''
    }]);
  };

  const updateLink = (index: number, field: 'title' | 'url', value: string) => {
    const updatedLinks = editLinks.map((link, i) => 
      i === index ? { ...link, [field]: value } : link
    );
    setEditLinks(updatedLinks);
  };

  const removeLink = (index: number) => {
    setEditLinks(editLinks.filter((_, i) => i !== index));
  };

  const toggleMetricCompletion = (appId: string, phase: 'crawl' | 'walk' | 'run', metricKey: string) => {
    const updatedApps = [...applications];
    const appIndex = updatedApps.findIndex(app => app.appId === appId);
    if (appIndex === -1) return;

    const phaseKey = phase + 'Metrics' as keyof ApplicationData;
    const updatedMetrics = { ...updatedApps[appIndex][phaseKey] as Record<string, ReliabilityMetric> };
    const currentMetric = updatedMetrics[metricKey];
    
    updatedMetrics[metricKey] = {
      ...currentMetric,
      completed: !currentMetric.completed,
      percentage: !currentMetric.completed ? 100 : 0,
      status: !currentMetric.completed ? 'completed' : 'not_started',
    };

    (updatedApps[appIndex] as any)[phaseKey] = updatedMetrics;
    setApplications(updatedApps);
  };

  const renderPortfolioOverview = () => (
    <Box>
      {/* Phase Tabs */}
      <Paper sx={{ mb: 3, border: '1px solid #e1e5e9', borderRadius: '8px', overflow: 'hidden' }}>
        <Tabs
          value={activePhaseTab}
          onChange={(e, newValue) => setActivePhaseTab(newValue)}
          sx={{
            borderBottom: '1px solid #e1e5e9',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '14px',
              minHeight: 56,
              color: '#6c757d',
              '&.Mui-selected': {
                color: '#0067b3',
                fontWeight: 600,
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#0067b3',
            },
          }}
        >
          <Tab 
            icon={<Speed />} 
            iconPosition="start" 
            label="Crawl Phase" 
            sx={{ flex: 1 }}
          />
          <Tab 
            icon={<TrendingUp />} 
            iconPosition="start" 
            label="Walk Phase" 
            sx={{ flex: 1 }}
          />
          <Tab 
            icon={<Assessment />} 
            iconPosition="start" 
            label="Run Phase" 
            sx={{ flex: 1 }}
          />
        </Tabs>

        {/* Crawl Phase Tab Content */}
        {activePhaseTab === 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '12px' }}>Platform Portfolio</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '12px' }}>App ID</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '12px' }}>Count of Apps</TableCell>
                {portfolioAggregations.length > 0 && Object.keys(portfolioAggregations[0].crawlProgress).map((metricKey) => (
                  <TableCell key={metricKey} sx={{ fontWeight: 600, fontSize: '12px' }}>
                    {metricKey}
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 600, fontSize: '12px' }}>Overall %</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {portfolioAggregations.map((agg) => (
                <React.Fragment key={agg.portfolio}>
                  {/* Portfolio-level aggregated row */}
                  <TableRow sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}>
                    <TableCell sx={{ fontSize: '13px', fontWeight: 500 }}>
                      <Button
                        onClick={() => handlePortfolioExpand(agg.portfolio)}
                        sx={{ 
                          textTransform: 'none', 
                          justifyContent: 'flex-start',
                          color: '#0067b3',
                          fontSize: '13px',
                          fontWeight: 500,
                        }}
                        startIcon={expandedPortfolios.includes(agg.portfolio) ? <ExpandMore /> : <ExpandMore sx={{ transform: 'rotate(-90deg)' }} />}
                      >
                        {agg.portfolio}
                      </Button>
                    </TableCell>
                    <TableCell sx={{ fontSize: '12px', textAlign: 'center', color: 'text.secondary' }}>-</TableCell>
                    <TableCell sx={{ fontSize: '13px', textAlign: 'center' }}>{agg.appCount}</TableCell>
                    {Object.keys(agg.crawlProgress).map((metric) => (
                      <TableCell key={metric} sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {getStatusIcon(agg.crawlProgress[metric].percentage, agg.crawlProgress[metric].implementationStatus)}
                            <Typography sx={{ 
                              fontSize: '12px', 
                              fontWeight: 600,
                              color: getStatusColor(agg.crawlProgress[metric].percentage, agg.crawlProgress[metric].implementationStatus)
                            }}>
                              {getStatusText(agg.crawlProgress[metric].percentage, agg.crawlProgress[metric].implementationStatus)}
                            </Typography>
                          </Box>
                          <Typography sx={{ 
                            fontSize: '11px',
                            color: getStatusColor(agg.crawlProgress[metric].percentage, agg.crawlProgress[metric].implementationStatus)
                          }}>
                            {agg.crawlProgress[metric].yesCount}/{agg.crawlProgress[metric].nonNACount}
                          </Typography>
                        </Box>
                      </TableCell>
                    ))}
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {getStatusIcon(agg.crawlOverallPercentage)}
                          <Typography sx={{ 
                            fontSize: '12px', 
                            fontWeight: 600,
                            color: getStatusColor(agg.crawlOverallPercentage)
                          }}>
                            {agg.crawlOverallPercentage}%
                          </Typography>
                        </Box>
                        <Typography sx={{ 
                          fontSize: '11px',
                          color: getStatusColor(agg.crawlOverallPercentage)
                        }}>
                          {Object.values(agg.crawlProgress).reduce((sum, m) => sum + m.yesCount, 0)}/
                          {Object.values(agg.crawlProgress).reduce((sum, m) => sum + m.nonNACount, 0)}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* Expanded platform and application rows */}
                  {expandedPortfolios.includes(agg.portfolio) && Object.values(agg.platformGroups).map((platformGroup) => (
                    <React.Fragment key={`${agg.portfolio}-${platformGroup.platform}`}>
                      {/* Platform row */}
                      <TableRow sx={{ backgroundColor: '#f1f3f4' }}>
                        <TableCell sx={{ fontSize: '12px', pl: 3, color: '#495057' }}>
                          <Button
                            onClick={() => handlePlatformExpand(`${agg.portfolio}-${platformGroup.platform}`)}
                            sx={{ 
                              textTransform: 'none', 
                              justifyContent: 'flex-start',
                              color: '#495057',
                              fontSize: '12px',
                              fontWeight: 500,
                              minWidth: 'auto',
                            }}
                            startIcon={expandedPlatforms.includes(`${agg.portfolio}-${platformGroup.platform}`) ? <ExpandMore /> : <ExpandMore sx={{ transform: 'rotate(-90deg)' }} />}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CloudQueue sx={{ fontSize: 16, color: '#495057' }} />
                              {platformGroup.platform}
                            </Box>
                          </Button>
                        </TableCell>
                        <TableCell sx={{ fontSize: '11px', textAlign: 'center', color: 'text.secondary' }}>-</TableCell>
                        <TableCell sx={{ fontSize: '11px', textAlign: 'center', color: '#6c757d' }}>
                          {platformGroup.appCount}
                        </TableCell>
                        {Object.keys(agg.crawlProgress).map((metric) => (
                          <TableCell key={metric} sx={{ textAlign: 'center' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {getStatusIcon(platformGroup.crawlProgress[metric]?.percentage || 0, platformGroup.crawlProgress[metric]?.implementationStatus)}
                                <Typography sx={{ 
                                  fontSize: '11px', 
                                  fontWeight: 500,
                                  color: getStatusColor(platformGroup.crawlProgress[metric]?.percentage || 0, platformGroup.crawlProgress[metric]?.implementationStatus)
                                }}>
                                  {getStatusText(platformGroup.crawlProgress[metric]?.percentage || 0, platformGroup.crawlProgress[metric]?.implementationStatus)}
                                </Typography>
                              </Box>
                              <Typography sx={{ 
                                fontSize: '10px',
                                color: getStatusColor(platformGroup.crawlProgress[metric]?.percentage || 0, platformGroup.crawlProgress[metric]?.implementationStatus)
                              }}>
                                {platformGroup.crawlProgress[metric]?.yesCount || 0}/{platformGroup.crawlProgress[metric]?.nonNACount || 0}
                              </Typography>
                            </Box>
                          </TableCell>
                        ))}
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {getStatusIcon(platformGroup.crawlOverallPercentage)}
                              <Typography sx={{ 
                                fontSize: '11px', 
                                fontWeight: 500,
                                color: getStatusColor(platformGroup.crawlOverallPercentage)
                              }}>
                                {platformGroup.crawlOverallPercentage}%
                              </Typography>
                            </Box>
                            <Typography sx={{ 
                              fontSize: '10px',
                              color: getStatusColor(platformGroup.crawlOverallPercentage)
                            }}>
                              {Object.values(platformGroup.crawlProgress).reduce((sum, m) => sum + m.yesCount, 0)}/
                              {Object.values(platformGroup.crawlProgress).reduce((sum, m) => sum + m.nonNACount, 0)}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Application rows under platform */}
                      {expandedPlatforms.includes(`${agg.portfolio}-${platformGroup.platform}`) && platformGroup.applications.map((app) => (
                        <TableRow key={`${agg.portfolio}-${platformGroup.platform}-${app.appId}`} sx={{ backgroundColor: '#f8f9fa' }}>
                          <TableCell sx={{ fontSize: '11px', pl: 5, color: '#6c757d' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Apps sx={{ fontSize: 14, color: '#6c757d' }} />
                              <Typography sx={{ fontSize: '11px', fontWeight: 500 }}>
                                {app.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: '10px', textAlign: 'center' }}>
                            {app.appId && app.appId !== 'N/A' ? (
                              <a 
                                href={`https://mac.maersk.io/application/${app.appId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ 
                                  color: '#0067b3', 
                                  textDecoration: 'underline',
                                  fontWeight: 500,
                                }}
                              >
                                {app.appId}
                              </a>
                            ) : (
                              <span style={{ fontWeight: 500, color: '#000' }}>N/A</span>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: '10px', textAlign: 'center', color: '#6c757d' }}>
                            -
                          </TableCell>
                          {Object.keys(agg.crawlProgress).map((metric) => (
                            <TableCell key={metric} sx={{ textAlign: 'center' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                {getStatusIcon(app.crawlMetrics[metric]?.percentage || 0, app.crawlMetrics[metric]?.implementationStatus)}
                                <Typography sx={{ 
                                  fontSize: '10px', 
                                  fontWeight: 500,
                                  color: getStatusColor(app.crawlMetrics[metric]?.percentage || 0, app.crawlMetrics[metric]?.implementationStatus)
                                }}>
                                  {getStatusText(app.crawlMetrics[metric]?.percentage || 0, app.crawlMetrics[metric]?.implementationStatus)}
                                </Typography>
                              </Box>
                            </TableCell>
                          ))}
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                              {(() => {
                                // Filter out NA metrics before calculating overall percentage
                                const crawlValues = Object.values(app.crawlMetrics)
                                  .filter(m => m.implementationStatus !== 'NA')
                                  .map(m => m.percentage || 0);
                                const overall = crawlValues.length > 0 
                                  ? parseFloat((crawlValues.reduce((sum, val) => sum + val, 0) / crawlValues.length).toFixed(2))
                                  : 0;
                                return (
                                  <>
                                    {getStatusIcon(overall)}
                                    <Typography sx={{ 
                                      fontSize: '10px', 
                                      fontWeight: 500,
                                      color: getStatusColor(overall)
                                    }}>
                                      {overall}%
                                    </Typography>
                                  </>
                                );
                              })()}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        )}

        {/* Walk Phase Tab Content */}
        {activePhaseTab === 1 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '12px' }}>Platform Portfolio</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '12px' }}>App ID</TableCell>
                {portfolioAggregations.length > 0 && Object.keys(portfolioAggregations[0].walkProgress)
                  .filter(metricKey => {
                    // Hide columns that are excluded from calculation
                    const sampleApp = portfolioAggregations[0].applications[0];
                    const isExcluded = sampleApp && sampleApp.walkMetrics[metricKey] && sampleApp.walkMetrics[metricKey].excludeFromCalculation;
                    return !isExcluded;
                  })
                  .map((metricKey) => (
                    <TableCell key={metricKey} sx={{ fontWeight: 600, fontSize: '12px' }}>
                      {metricKey}
                    </TableCell>
                  ))}
                <TableCell sx={{ fontWeight: 600, fontSize: '12px' }}>Overall %</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {portfolioAggregations.map((agg) => (
                <React.Fragment key={agg.portfolio}>
                  {/* Portfolio-level aggregated row */}
                  <TableRow sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}>
                    <TableCell sx={{ fontSize: '13px', fontWeight: 500 }}>
                      <Button
                        onClick={() => handlePortfolioExpand(agg.portfolio)}
                        sx={{ 
                          textTransform: 'none', 
                          justifyContent: 'flex-start',
                          color: '#0067b3',
                          fontSize: '13px',
                          fontWeight: 500,
                        }}
                        startIcon={expandedPortfolios.includes(agg.portfolio) ? <ExpandMore /> : <ExpandMore sx={{ transform: 'rotate(-90deg)' }} />}
                      >
                        {agg.portfolio}
                      </Button>
                    </TableCell>
                    <TableCell sx={{ fontSize: '12px', textAlign: 'center', color: 'text.secondary' }}>-</TableCell>
                    {Object.keys(agg.walkProgress)
                      .filter(metric => {
                        // Hide columns that are excluded from calculation
                        const sampleApp = agg.applications[0];
                        const isExcluded = sampleApp && sampleApp.walkMetrics[metric] && sampleApp.walkMetrics[metric].excludeFromCalculation;
                        return !isExcluded;
                      })
                      .map((metric) => (
                        <TableCell key={metric} sx={{ textAlign: 'center' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {getStatusIcon(agg.walkProgress[metric].percentage, agg.walkProgress[metric].implementationStatus)}
                              <Typography sx={{ 
                                fontSize: '12px', 
                                fontWeight: 600,
                                color: getStatusColor(agg.walkProgress[metric].percentage, agg.walkProgress[metric].implementationStatus)
                              }}>
                                {getStatusText(agg.walkProgress[metric].percentage, agg.walkProgress[metric].implementationStatus)}
                              </Typography>
                            </Box>
                            <Typography sx={{ 
                              fontSize: '11px',
                              color: getStatusColor(agg.walkProgress[metric].percentage, agg.walkProgress[metric].implementationStatus)
                            }}>
                              {agg.walkProgress[metric].yesCount}/{agg.walkProgress[metric].nonNACount}
                            </Typography>
                          </Box>
                        </TableCell>
                      ))}
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {getStatusIcon(agg.walkOverallPercentage)}
                          <Typography sx={{ 
                            fontSize: '12px', 
                            fontWeight: 600,
                            color: getStatusColor(agg.walkOverallPercentage)
                          }}>
                            {agg.walkOverallPercentage}%
                          </Typography>
                        </Box>
                        <Typography sx={{ 
                          fontSize: '11px',
                          color: getStatusColor(agg.walkOverallPercentage)
                        }}>
                          {Object.values(agg.walkProgress).filter(m => !Object.keys(agg.walkProgress).some(key => {
                            const sampleApp = agg.applications[0];
                            return sampleApp?.walkMetrics[key]?.excludeFromCalculation && agg.walkProgress[key] === m;
                          })).reduce((sum, m) => sum + m.yesCount, 0)}/
                          {Object.values(agg.walkProgress).filter(m => !Object.keys(agg.walkProgress).some(key => {
                            const sampleApp = agg.applications[0];
                            return sampleApp?.walkMetrics[key]?.excludeFromCalculation && agg.walkProgress[key] === m;
                          })).reduce((sum, m) => sum + m.nonNACount, 0)}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* Expanded platform and application rows */}
                  {expandedPortfolios.includes(agg.portfolio) && Object.values(agg.platformGroups).map((platformGroup) => (
                    <React.Fragment key={`${agg.portfolio}-${platformGroup.platform}`}>
                      {/* Platform row */}
                      <TableRow sx={{ backgroundColor: '#f1f3f4' }}>
                        <TableCell sx={{ fontSize: '12px', pl: 3, color: '#495057' }}>
                          <Button
                            onClick={() => handlePlatformExpand(`${agg.portfolio}-${platformGroup.platform}`)}
                            sx={{ 
                              textTransform: 'none', 
                              justifyContent: 'flex-start',
                              color: '#495057',
                              fontSize: '12px',
                              fontWeight: 500,
                              minWidth: 'auto',
                            }}
                            startIcon={expandedPlatforms.includes(`${agg.portfolio}-${platformGroup.platform}`) ? <ExpandMore /> : <ExpandMore sx={{ transform: 'rotate(-90deg)' }} />}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CloudQueue sx={{ fontSize: 16, color: '#495057' }} />
                              {platformGroup.platform}
                            </Box>
                          </Button>
                        </TableCell>
                        <TableCell sx={{ fontSize: '11px', textAlign: 'center', color: 'text.secondary' }}>-</TableCell>
                        {Object.keys(agg.walkProgress)
                          .filter(metric => {
                            // Hide columns that are excluded from calculation
                            const sampleApp = platformGroup.applications[0];
                            const isExcluded = sampleApp && sampleApp.walkMetrics[metric] && sampleApp.walkMetrics[metric].excludeFromCalculation;
                            return !isExcluded;
                          })
                          .map((metric) => (
                            <TableCell key={metric} sx={{ textAlign: 'center' }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {getStatusIcon(platformGroup.walkProgress[metric]?.percentage || 0, platformGroup.walkProgress[metric]?.implementationStatus)}
                                  <Typography sx={{ 
                                    fontSize: '11px', 
                                    fontWeight: 500,
                                    color: getStatusColor(platformGroup.walkProgress[metric]?.percentage || 0, platformGroup.walkProgress[metric]?.implementationStatus)
                                  }}>
                                    {getStatusText(platformGroup.walkProgress[metric]?.percentage || 0, platformGroup.walkProgress[metric]?.implementationStatus)}
                                  </Typography>
                                </Box>
                                <Typography sx={{ 
                                  fontSize: '10px',
                                  color: getStatusColor(platformGroup.walkProgress[metric]?.percentage || 0, platformGroup.walkProgress[metric]?.implementationStatus)
                                }}>
                                  {platformGroup.walkProgress[metric]?.yesCount || 0}/{platformGroup.walkProgress[metric]?.nonNACount || 0}
                                </Typography>
                              </Box>
                            </TableCell>
                          ))}
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {getStatusIcon(platformGroup.walkOverallPercentage)}
                              <Typography sx={{ 
                                fontSize: '11px', 
                                fontWeight: 500,
                                color: getStatusColor(platformGroup.walkOverallPercentage)
                              }}>
                                {platformGroup.walkOverallPercentage}%
                              </Typography>
                            </Box>
                            <Typography sx={{ 
                              fontSize: '10px',
                              color: getStatusColor(platformGroup.walkOverallPercentage)
                            }}>
                              {Object.values(platformGroup.walkProgress).filter(m => !Object.keys(platformGroup.walkProgress).some(key => {
                                const sampleApp = platformGroup.applications[0];
                                return sampleApp?.walkMetrics[key]?.excludeFromCalculation && platformGroup.walkProgress[key] === m;
                              })).reduce((sum, m) => sum + m.yesCount, 0)}/
                              {Object.values(platformGroup.walkProgress).filter(m => !Object.keys(platformGroup.walkProgress).some(key => {
                                const sampleApp = platformGroup.applications[0];
                                return sampleApp?.walkMetrics[key]?.excludeFromCalculation && platformGroup.walkProgress[key] === m;
                              })).reduce((sum, m) => sum + m.nonNACount, 0)}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Application rows under platform */}
                      {expandedPlatforms.includes(`${agg.portfolio}-${platformGroup.platform}`) && platformGroup.applications.map((app) => (
                        <TableRow key={`${agg.portfolio}-${platformGroup.platform}-${app.appId}`} sx={{ backgroundColor: '#f8f9fa' }}>
                          <TableCell sx={{ fontSize: '11px', pl: 5, color: '#6c757d' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Apps sx={{ fontSize: 14, color: '#6c757d' }} />
                              <Typography sx={{ fontSize: '11px', fontWeight: 500 }}>
                                {app.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: '10px', textAlign: 'center' }}>
                            {app.appId && app.appId !== 'N/A' ? (
                              <a 
                                href={`https://mac.maersk.io/application/${app.appId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ 
                                  color: '#0067b3', 
                                  textDecoration: 'underline',
                                  fontWeight: 500,
                                }}
                              >
                                {app.appId}
                              </a>
                            ) : (
                              <span style={{ fontWeight: 500, color: '#000' }}>N/A</span>
                            )}
                          </TableCell>
                          {Object.keys(agg.walkProgress)
                            .filter(metric => {
                              // Hide columns that are excluded from calculation
                              return !app.walkMetrics[metric]?.excludeFromCalculation;
                            })
                            .map((metric) => (
                              <TableCell key={metric} sx={{ textAlign: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                  {getStatusIcon(app.walkMetrics[metric]?.percentage || 0, app.walkMetrics[metric]?.implementationStatus)}
                                  <Typography sx={{ 
                                    fontSize: '10px', 
                                    fontWeight: 500,
                                    color: getStatusColor(app.walkMetrics[metric]?.percentage || 0, app.walkMetrics[metric]?.implementationStatus)
                                  }}>
                                    {getStatusText(app.walkMetrics[metric]?.percentage || 0, app.walkMetrics[metric]?.implementationStatus)}
                                  </Typography>
                                </Box>
                              </TableCell>
                            ))}
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                              {(() => {
                                // Filter out NA metrics and reference metrics (excludeFromCalculation) for Walk phase
                                const walkValues = Object.values(app.walkMetrics)
                                  .filter(m => !m.excludeFromCalculation && m.implementationStatus !== 'NA')
                                  .map(m => m.percentage || 0);
                                const overall = walkValues.length > 0 
                                  ? parseFloat((walkValues.reduce((sum, val) => sum + val, 0) / walkValues.length).toFixed(2))
                                  : 0;
                                return (
                                  <>
                                    {getStatusIcon(overall)}
                                    <Typography sx={{ 
                                      fontSize: '10px', 
                                      fontWeight: 500,
                                      color: getStatusColor(overall)
                                    }}>
                                      {overall}%
                                    </Typography>
                                  </>
                                );
                              })()}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        )}

        {/* Run Phase Tab Content */}
        {activePhaseTab === 2 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '12px' }}>Platform Portfolio</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '12px' }}>App ID</TableCell>
                {portfolioAggregations.length > 0 && Object.keys(portfolioAggregations[0].runProgress).map((metricKey) => (
                  <TableCell key={metricKey} sx={{ fontWeight: 600, fontSize: '12px' }}>
                    {metricKey}
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 600, fontSize: '12px' }}>Overall %</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {portfolioAggregations.map((agg) => (
                <React.Fragment key={agg.portfolio}>
                  {/* Portfolio-level aggregated row */}
                  <TableRow sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}>
                    <TableCell sx={{ fontSize: '13px', fontWeight: 500 }}>
                      <Button
                        onClick={() => handlePortfolioExpand(agg.portfolio)}
                        sx={{ 
                          textTransform: 'none', 
                          justifyContent: 'flex-start',
                          color: '#0067b3',
                          fontSize: '13px',
                          fontWeight: 500,
                        }}
                        startIcon={expandedPortfolios.includes(agg.portfolio) ? <ExpandMore /> : <ExpandMore sx={{ transform: 'rotate(-90deg)' }} />}
                      >
                        {agg.portfolio}
                      </Button>
                    </TableCell>
                    <TableCell sx={{ fontSize: '12px', textAlign: 'center', color: 'text.secondary' }}>-</TableCell>
                    {Object.keys(agg.runProgress).map((metric) => (
                      <TableCell key={metric} sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {getStatusIcon(agg.runProgress[metric].percentage, agg.runProgress[metric].implementationStatus)}
                            <Typography sx={{ 
                              fontSize: '12px', 
                              fontWeight: 600,
                              color: getStatusColor(agg.runProgress[metric].percentage, agg.runProgress[metric].implementationStatus)
                            }}>
                              {getStatusText(agg.runProgress[metric].percentage, agg.runProgress[metric].implementationStatus)}
                            </Typography>
                          </Box>
                          <Typography sx={{ 
                            fontSize: '11px',
                            color: getStatusColor(agg.runProgress[metric].percentage, agg.runProgress[metric].implementationStatus)
                          }}>
                            {agg.runProgress[metric].yesCount}/{agg.runProgress[metric].nonNACount}
                          </Typography>
                        </Box>
                      </TableCell>
                    ))}
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {getStatusIcon(agg.runOverallPercentage)}
                          <Typography sx={{ 
                            fontSize: '12px', 
                            fontWeight: 600,
                            color: getStatusColor(agg.runOverallPercentage)
                          }}>
                            {agg.runOverallPercentage}%
                          </Typography>
                        </Box>
                        <Typography sx={{ 
                          fontSize: '11px',
                          color: getStatusColor(agg.runOverallPercentage)
                        }}>
                          {Object.values(agg.runProgress).reduce((sum, m) => sum + m.yesCount, 0)}/
                          {Object.values(agg.runProgress).reduce((sum, m) => sum + m.nonNACount, 0)}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* Expanded platform and application rows */}
                  {expandedPortfolios.includes(agg.portfolio) && Object.values(agg.platformGroups).map((platformGroup) => (
                    <React.Fragment key={`${agg.portfolio}-${platformGroup.platform}`}>
                      {/* Platform row */}
                      <TableRow sx={{ backgroundColor: '#f1f3f4' }}>
                        <TableCell sx={{ fontSize: '12px', pl: 3, color: '#495057' }}>
                          <Button
                            onClick={() => handlePlatformExpand(`${agg.portfolio}-${platformGroup.platform}`)}
                            sx={{ 
                              textTransform: 'none', 
                              justifyContent: 'flex-start',
                              color: '#495057',
                              fontSize: '12px',
                              fontWeight: 500,
                              minWidth: 'auto',
                            }}
                            startIcon={expandedPlatforms.includes(`${agg.portfolio}-${platformGroup.platform}`) ? <ExpandMore /> : <ExpandMore sx={{ transform: 'rotate(-90deg)' }} />}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CloudQueue sx={{ fontSize: 16, color: '#495057' }} />
                              {platformGroup.platform}
                            </Box>
                          </Button>
                        </TableCell>
                        <TableCell sx={{ fontSize: '11px', textAlign: 'center', color: 'text.secondary' }}>-</TableCell>
                        {Object.keys(agg.runProgress).map((metric) => (
                          <TableCell key={metric} sx={{ textAlign: 'center' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {getStatusIcon(platformGroup.runProgress[metric]?.percentage || 0, platformGroup.runProgress[metric]?.implementationStatus)}
                                <Typography sx={{ 
                                  fontSize: '11px', 
                                  fontWeight: 500,
                                  color: getStatusColor(platformGroup.runProgress[metric]?.percentage || 0, platformGroup.runProgress[metric]?.implementationStatus)
                                }}>
                                  {getStatusText(platformGroup.runProgress[metric]?.percentage || 0, platformGroup.runProgress[metric]?.implementationStatus)}
                                </Typography>
                              </Box>
                              <Typography sx={{ 
                                fontSize: '10px',
                                color: getStatusColor(platformGroup.runProgress[metric]?.percentage || 0, platformGroup.runProgress[metric]?.implementationStatus)
                              }}>
                                {platformGroup.runProgress[metric]?.yesCount || 0}/{platformGroup.runProgress[metric]?.nonNACount || 0}
                              </Typography>
                            </Box>
                          </TableCell>
                        ))}
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {getStatusIcon(platformGroup.runOverallPercentage)}
                              <Typography sx={{ 
                                fontSize: '11px', 
                                fontWeight: 500,
                                color: getStatusColor(platformGroup.runOverallPercentage)
                              }}>
                                {platformGroup.runOverallPercentage}%
                              </Typography>
                            </Box>
                            <Typography sx={{ 
                              fontSize: '10px',
                              color: getStatusColor(platformGroup.runOverallPercentage)
                            }}>
                              {Object.values(platformGroup.runProgress).reduce((sum, m) => sum + m.yesCount, 0)}/
                              {Object.values(platformGroup.runProgress).reduce((sum, m) => sum + m.nonNACount, 0)}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Application rows under platform */}
                      {expandedPlatforms.includes(`${agg.portfolio}-${platformGroup.platform}`) && platformGroup.applications.map((app) => (
                        <TableRow key={`${agg.portfolio}-${platformGroup.platform}-${app.appId}`} sx={{ backgroundColor: '#f8f9fa' }}>
                          <TableCell sx={{ fontSize: '11px', pl: 5, color: '#6c757d' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Apps sx={{ fontSize: 14, color: '#6c757d' }} />
                              <Typography sx={{ fontSize: '11px', fontWeight: 500 }}>
                                {app.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: '10px', textAlign: 'center' }}>
                            {app.appId && app.appId !== 'N/A' ? (
                              <a 
                                href={`https://mac.maersk.io/application/${app.appId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ 
                                  color: '#0067b3', 
                                  textDecoration: 'underline',
                                  fontWeight: 500,
                                }}
                              >
                                {app.appId}
                              </a>
                            ) : (
                              <span style={{ fontWeight: 500, color: '#000' }}>N/A</span>
                            )}
                          </TableCell>
                          {Object.keys(agg.runProgress).map((metric) => (
                            <TableCell key={metric} sx={{ textAlign: 'center' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                {getStatusIcon(app.runMetrics[metric]?.percentage || 0, app.runMetrics[metric]?.implementationStatus)}
                                <Typography sx={{ 
                                  fontSize: '10px', 
                                  fontWeight: 500,
                                  color: getStatusColor(app.runMetrics[metric]?.percentage || 0, app.runMetrics[metric]?.implementationStatus)
                                }}>
                                  {getStatusText(app.runMetrics[metric]?.percentage || 0, app.runMetrics[metric]?.implementationStatus)}
                                </Typography>
                              </Box>
                            </TableCell>
                          ))}
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                              {(() => {
                                // Filter out NA metrics before calculating overall percentage
                                const runValues = Object.values(app.runMetrics)
                                  .filter(m => m.implementationStatus !== 'NA')
                                  .map(m => m.percentage || 0);
                                const overall = runValues.length > 0 
                                  ? parseFloat((runValues.reduce((sum, val) => sum + val, 0) / runValues.length).toFixed(2))
                                  : 0;
                                return (
                                  <>
                                    {getStatusIcon(overall)}
                                    <Typography sx={{ 
                                      fontSize: '10px', 
                                      fontWeight: 500,
                                      color: getStatusColor(overall)
                                    }}>
                                      {overall}%
                                    </Typography>
                                  </>
                                );
                              })()}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        )}
      </Paper>
    </Box>
  );

  const renderApplicationView = () => {
    const app = applications.find(a => a.appId === selectedApp);
    if (!app) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Please select an application from the sidebar
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Use the Application filter on the left to choose an application to view detailed reliability metrics.
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        {/* Loading indicator for links */}
        {loadingLinks && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="textSecondary">
              Loading parameter links...
            </Typography>
          </Box>
        )}
        
        {/* Maturity Score Overview */}
        <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f8f9fa', border: '1px solid #e1e5e9', borderRadius: '8px' }}>
          <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600, color: '#2c3e50' }}>
            Reliability Assessment Summary
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Current reliability maturity across all stages
          </Typography>
          
          <Grid container spacing={3}>
            {/* Crawl Progress */}
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#2c3e50' }}>
                    Crawl
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#6f42c1' }}>
                    {calculateStagePercentage(app.crawlMetrics).toFixed(2)}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={calculateStagePercentage(app.crawlMetrics)} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 1,
                    backgroundColor: '#e1e5e9',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#6f42c1',
                      borderRadius: 1,
                    }
                  }}
                />
              </Box>
            </Grid>

            {/* Walk Progress */}
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#2c3e50' }}>
                    Walk
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#28a745' }}>
                    {calculateStagePercentage(app.walkMetrics).toFixed(2)}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={calculateStagePercentage(app.walkMetrics)} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 1,
                    backgroundColor: '#e1e5e9',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#28a745',
                      borderRadius: 1,
                    }
                  }}
                />
              </Box>
            </Grid>

            {/* Run Progress */}
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#2c3e50' }}>
                    Run
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#9c27b0' }}>
                    {calculateStagePercentage(app.runMetrics).toFixed(2)}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={calculateStagePercentage(app.runMetrics)} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 1,
                    backgroundColor: '#e1e5e9',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#9c27b0',
                      borderRadius: 1,
                    }
                  }}
                />
              </Box>
            </Grid>

            {/* Overall Progress */}
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#2c3e50' }}>
                    Overall
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#fd7e14' }}>
                    {(((calculateStagePercentage(app.crawlMetrics) + calculateStagePercentage(app.walkMetrics) + calculateStagePercentage(app.runMetrics)) / 3).toFixed(2))}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(calculateStagePercentage(app.crawlMetrics) + calculateStagePercentage(app.walkMetrics) + calculateStagePercentage(app.runMetrics)) / 3} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 1,
                    backgroundColor: '#e1e5e9',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#fd7e14',
                      borderRadius: 1,
                    }
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* App Header */}
        <Paper sx={{ p: 3, mb: 3, border: '1px solid #e1e5e9', borderRadius: '8px' }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Typography variant="subtitle2" fontWeight="bold" color="textSecondary">Application</Typography>
              <Typography variant="body1">{app.name}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Typography variant="subtitle2" fontWeight="bold" color="textSecondary">App ID</Typography>
              <a 
                href={`https://mac.maersk.io/application/${app.appId}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ 
                  color: '#0067b3', 
                  textDecoration: 'underline',
                  fontWeight: 500,
                  fontSize: '1rem',
                }}
              >
                {app.appId}
              </a>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Typography variant="subtitle2" fontWeight="bold" color="textSecondary">Portfolio</Typography>
              <Typography variant="body1">{app.portfolio}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Typography variant="subtitle2" fontWeight="bold" color="textSecondary">Platform</Typography>
              <Typography variant="body1">{app.platform}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Typography variant="subtitle2" fontWeight="bold" color="textSecondary">Owners</Typography>
              {(app.owners || app.emSem)
                ? (app.owners || app.emSem).split(',').map((name: string, i: number) => {
                    const emails = (app.ownersRaw || app.emSem || '').split(',');
                    const email = emails[i]?.trim() || '';
                    return (
                      <Tooltip key={i} title={email || 'No email available'} arrow placement="top">
                        <Typography variant="body2" sx={{ wordBreak: 'break-all', lineHeight: 1.5, cursor: 'help' }}>
                          {name.trim()}
                        </Typography>
                      </Tooltip>
                    );
                  })
                : <Typography variant="body1">-</Typography>
              }
            </Grid>
          </Grid>
        </Paper>

        {/* Phase Navigation Tabs */}
        <Paper sx={{ mb: 3, border: '1px solid #e1e5e9', borderRadius: '8px', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', width: '100%' }}>
            {/* Crawl Tab */}
            <Box 
              onClick={() => setActivePhaseTab(0)}
              sx={{ 
                width: '33.33%',
                p: 2, 
                backgroundColor: activePhaseTab === 0 ? '#ffffff' : '#f8f9fa',
                borderRight: '1px solid #e1e5e9',
                borderBottom: '1px solid #e1e5e9',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: '#ffffff',
                },
                ...(activePhaseTab === 0 && {
                  boxShadow: 'inset 0 -3px 0 0 #0067b3',
                }),
              }}
            >
              <Typography 
                variant="body1" 
                fontWeight={activePhaseTab === 0 ? 600 : 500} 
                sx={{ 
                  color: activePhaseTab === 0 ? '#0067b3' : '#6c757d', 
                  textAlign: 'center' 
                }}
              >
                Crawl ({calculateStagePercentage(app.crawlMetrics).toFixed(2)}%)
              </Typography>
            </Box>

            {/* Walk Tab */}
            <Box 
              onClick={() => setActivePhaseTab(1)}
              sx={{ 
                width: '33.33%',
                p: 2, 
                backgroundColor: activePhaseTab === 1 ? '#ffffff' : '#f8f9fa',
                borderRight: '1px solid #e1e5e9',
                borderBottom: '1px solid #e1e5e9',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: '#ffffff',
                },
                ...(activePhaseTab === 1 && {
                  boxShadow: 'inset 0 -3px 0 0 #0067b3',
                }),
              }}
            >
              <Typography 
                variant="body1" 
                fontWeight={activePhaseTab === 1 ? 600 : 500} 
                sx={{ 
                  color: activePhaseTab === 1 ? '#0067b3' : '#6c757d', 
                  textAlign: 'center' 
                }}
              >
                Walk ({calculateStagePercentage(app.walkMetrics).toFixed(2)}%)
              </Typography>
            </Box>

            {/* Run Tab */}
            <Box 
              onClick={() => setActivePhaseTab(2)}
              sx={{ 
                width: '33.34%',
                p: 2, 
                backgroundColor: activePhaseTab === 2 ? '#ffffff' : '#f8f9fa',
                borderBottom: '1px solid #e1e5e9',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: '#ffffff',
                },
                ...(activePhaseTab === 2 && {
                  boxShadow: 'inset 0 -3px 0 0 #0067b3',
                }),
              }}
            >
              <Typography 
                variant="body1" 
                fontWeight={activePhaseTab === 2 ? 600 : 500} 
                sx={{ 
                  color: activePhaseTab === 2 ? '#0067b3' : '#6c757d', 
                  textAlign: 'center' 
                }}
              >
                Run ({calculateStagePercentage(app.runMetrics).toFixed(2)}%)
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Phase Content */}
        <Paper sx={{ mb: 3, border: '1px solid #e1e5e9', borderRadius: '8px', overflow: 'hidden' }}>
          {/* Crawl Phase Content */}
          {activePhaseTab === 0 && (
            <Box sx={{ p: 3 }}>
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Speed sx={{ color: '#0067b3', fontSize: 28 }} />
                <Typography variant="h6" sx={{ 
                  fontWeight: 600, 
                  color: '#0067b3',
                  fontFamily: "'Maersk Text', 'Inter', 'Segoe UI', Arial, sans-serif",
                }}>
                  Crawl Phase
                </Typography>
                <Chip 
                  label={`${calculateStagePercentage(app.crawlMetrics).toFixed(2)}%`}
                  size="small"
                  sx={{ 
                    backgroundColor: getStatusColor(calculateStagePercentage(app.crawlMetrics)),
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '12px'
                  }}
                />
                <Typography variant="body2" sx={{ 
                  color: '#6c757d',
                  fontStyle: 'italic',
                  ml: 1,
                }}>
                  Foundation - Basic monitoring, alerting, and operational readiness
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {Object.entries(app.crawlMetrics).map(([key, metric]) => (
                  <Grid item xs={12} md={6} key={key}>
                    <Box sx={{ 
                      p: 2, 
                      border: '1px solid #e1e5e9', 
                      borderRadius: '6px', 
                      backgroundColor: metric.implementationStatus === 'No' ? '#ffebee' : 
                                       metric.implementationStatus === 'NA' ? '#e3f2fd' : '#fafafa', 
                      height: '100%' 
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => toggleMetricCompletion(app.appId, 'crawl', key)}
                            sx={{ p: 0.3 }}
                          >
                            {metric.implementationStatus === 'NA' ? (
                              <Help sx={{ color: '#2196f3', fontSize: 18 }} />
                            ) : metric.completed ? (
                              <CheckCircle sx={{ color: '#27ae60', fontSize: 18 }} />
                            ) : (
                              <Cancel sx={{ color: '#e74c3c', fontSize: 18 }} />
                            )}
                          </IconButton>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '14px' }}>{metric.name}</Typography>
                          {(metric.definition || metric.implementationGuidance || metric.successCriteria) && (
                            <Tooltip
                              title={
                                <Box sx={{ maxWidth: 400 }}>
                                  {metric.definition && (
                                    <Box sx={{ mb: 2 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#fff', mb: 1 }}>
                                        Definition:
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: '#fff', lineHeight: 1.4 }}>
                                        {metric.definition}
                                      </Typography>
                                    </Box>
                                  )}
                                  {metric.implementationGuidance && (
                                    <Box sx={{ mb: 2 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#fff', mb: 1 }}>
                                        Implementation Guidance:
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: '#fff', lineHeight: 1.4 }}>
                                        {metric.implementationGuidance}
                                      </Typography>
                                    </Box>
                                  )}
                                  {metric.successCriteria && (
                                    <Box>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#fff', mb: 1 }}>
                                        Success Criteria:
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: '#fff', lineHeight: 1.4 }}>
                                        {metric.successCriteria}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              }
                              arrow
                              placement="top"
                              sx={{
                                '& .MuiTooltip-tooltip': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                  maxWidth: 'none',
                                  fontSize: '12px',
                                },
                                '& .MuiTooltip-arrow': {
                                  color: 'rgba(0, 0, 0, 0.9)',
                                },
                              }}
                            >
                              <Help sx={{ fontSize: 16, color: '#0067b3', cursor: 'pointer' }} />
                            </Tooltip>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip 
                            size="small" 
                            label={metric.implementationStatus || 'No'}
                            sx={{ 
                              backgroundColor: metric.implementationStatus === 'Yes' ? '#27ae60 !important' : 
                                               metric.implementationStatus === 'NA' ? '#6c757d !important' : '#e74c3c !important',
                              color: 'white !important',
                              '& .MuiChip-label': {
                                color: 'white',
                                fontSize: '11px',
                                fontWeight: 600,
                              }
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => openEditModal(app.appId, 'crawl', key, metric)}
                            sx={{ p: 0.5 }}
                          >
                            <EditIcon sx={{ fontSize: 16, color: '#0067b3' }} />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      
                      {/* Remarks */}
                      {metric.remarks && (
                        <Box sx={{ display: 'flex', alignItems: 'start', gap: 1, mb: 2 }}>
                          <CommentIcon sx={{ fontSize: 14, color: '#0067b3', mt: 0.2 }} />
                          <Typography variant="body2" sx={{ color: '#2c3e50', fontSize: '13px' }}>
                            {metric.remarks}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Links */}
                      {metric.links && metric.links.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'start', gap: 1 }}>
                          <OpenInNew sx={{ fontSize: 14, color: '#0067b3', mt: 0.2 }} />
                          <Box>
                            {metric.links.map((link, index) => (
                              <Typography 
                                key={index}
                                variant="body2" 
                                component="a"
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ 
                                  color: '#0067b3', 
                                  textDecoration: 'none',
                                  display: 'block',
                                  fontSize: '13px',
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                              >
                                {link.title || link.url}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Walk Phase Content */}
          {activePhaseTab === 1 && (
            <Box sx={{ p: 3 }}>
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUp sx={{ color: '#856404', fontSize: 28 }} />
                <Typography variant="h6" sx={{ 
                  fontWeight: 600, 
                  color: '#856404',
                  fontFamily: "'Maersk Text', 'Inter', 'Segoe UI', Arial, sans-serif",
                }}>
                  Walk Phase
                </Typography>
                <Chip 
                  label={`${calculateStagePercentage(app.walkMetrics).toFixed(2)}%`}
                  size="small"
                  sx={{ 
                    backgroundColor: getStatusColor(calculateStagePercentage(app.walkMetrics)),
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '12px'
                  }}
                />
                <Typography variant="body2" sx={{ 
                  color: '#6c757d',
                  fontStyle: 'italic',
                  ml: 1,
                }}>
                  Optimization - Advanced practices, testing, and reliability engineering
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {Object.entries(app.walkMetrics).map(([key, metric]) => (
                  <Grid item xs={12} md={6} key={key}>
                    <Box sx={{ 
                      p: 2, 
                      border: '1px solid #e1e5e9', 
                      borderRadius: '6px', 
                      backgroundColor: metric.implementationStatus === 'No' ? '#ffebee' : 
                                       metric.implementationStatus === 'NA' ? '#e3f2fd' : '#fafafa', 
                      height: '100%' 
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => toggleMetricCompletion(app.appId, 'walk', key)}
                            sx={{ p: 0.3 }}
                          >
                            {metric.implementationStatus === 'NA' ? (
                              <Help sx={{ color: '#2196f3', fontSize: 18 }} />
                            ) : metric.completed ? (
                              <CheckCircle sx={{ color: '#27ae60', fontSize: 18 }} />
                            ) : (
                              <Cancel sx={{ color: '#e74c3c', fontSize: 18 }} />
                            )}
                          </IconButton>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '14px' }}>
                            {metric.name}
                            {metric.excludeFromCalculation && (
                              <Typography component="span" sx={{ ml: 0.5, fontSize: '10px', color: '#6c757d', fontStyle: 'italic' }}>
                                (REF)
                              </Typography>
                            )}
                          </Typography>
                          {(metric.definition || metric.implementationGuidance || metric.successCriteria) && (
                            <Tooltip
                              title={
                                <Box sx={{ maxWidth: 400 }}>
                                  {metric.definition && (
                                    <Box sx={{ mb: 2 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#fff', mb: 1 }}>
                                        Definition:
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: '#fff', lineHeight: 1.4 }}>
                                        {metric.definition}
                                      </Typography>
                                    </Box>
                                  )}
                                  {metric.implementationGuidance && (
                                    <Box sx={{ mb: 2 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#fff', mb: 1 }}>
                                        Implementation Guidance:
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: '#fff', lineHeight: 1.4 }}>
                                        {metric.implementationGuidance}
                                      </Typography>
                                    </Box>
                                  )}
                                  {metric.successCriteria && (
                                    <Box>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#fff', mb: 1 }}>
                                        Success Criteria:
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: '#fff', lineHeight: 1.4 }}>
                                        {metric.successCriteria}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              }
                              arrow
                              placement="top"
                              sx={{
                                '& .MuiTooltip-tooltip': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                  maxWidth: 'none',
                                  fontSize: '12px',
                                },
                                '& .MuiTooltip-arrow': {
                                  color: 'rgba(0, 0, 0, 0.9)',
                                },
                              }}
                            >
                              <Help sx={{ fontSize: 16, color: '#0067b3', cursor: 'pointer' }} />
                            </Tooltip>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip 
                            size="small" 
                            label={metric.implementationStatus || 'No'}
                            sx={{ 
                              backgroundColor: metric.implementationStatus === 'Yes' ? '#27ae60 !important' : 
                                               metric.implementationStatus === 'NA' ? '#6c757d !important' : '#e74c3c !important',
                              color: 'white !important',
                              '& .MuiChip-label': {
                                color: 'white',
                                fontSize: '11px',
                                fontWeight: 600,
                              }
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => openEditModal(app.appId, 'walk', key, metric)}
                            sx={{ p: 0.5 }}
                          >
                            <EditIcon sx={{ fontSize: 16, color: '#0067b3' }} />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      {/* Reference Note for excluded metrics */}
                      {metric.excludeFromCalculation && (
                        <Box sx={{ 
                          mb: 1, 
                          p: 0.5, 
                          px: 1,
                          backgroundColor: '#fff3cd', 
                          borderLeft: '2px solid #ffc107',
                          borderRadius: '3px'
                        }}>
                          <Typography variant="caption" sx={{ 
                            color: '#856404',
                            fontSize: '10px',
                            fontStyle: 'italic',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            lineHeight: 1.4
                          }}>
                            <InfoIcon sx={{ fontSize: 11 }} />
                            Not part of Overall % - Tracking only
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Remarks */}
                      {metric.remarks && (
                        <Box sx={{ display: 'flex', alignItems: 'start', gap: 1, mb: 2 }}>
                          <CommentIcon sx={{ fontSize: 14, color: '#0067b3', mt: 0.2 }} />
                          <Typography variant="body2" sx={{ color: '#2c3e50', fontSize: '13px' }}>
                            {metric.remarks}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Links */}
                      {metric.links && metric.links.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'start', gap: 1 }}>
                          <OpenInNew sx={{ fontSize: 14, color: '#0067b3', mt: 0.2 }} />
                          <Box>
                            {metric.links.map((link, index) => (
                              <Typography 
                                key={index}
                                variant="body2" 
                                component="a"
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ 
                                  color: '#0067b3', 
                                  textDecoration: 'none',
                                  display: 'block',
                                  fontSize: '13px',
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                              >
                                {link.title || link.url}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Run Phase Content */}
          {activePhaseTab === 2 && (
            <Box sx={{ p: 3 }}>
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Assessment sx={{ color: '#0c5460', fontSize: 28 }} />
                <Typography variant="h6" sx={{ 
                  fontWeight: 600, 
                  color: '#0c5460',
                  fontFamily: "'Maersk Text', 'Inter', 'Segoe UI', Arial, sans-serif",
                }}>
                  Run Phase
                </Typography>
                <Chip 
                  label={`${calculateStagePercentage(app.runMetrics).toFixed(2)}%`}
                  size="small"
                  sx={{ 
                    backgroundColor: getStatusColor(calculateStagePercentage(app.runMetrics)),
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '12px'
                  }}
                />
                <Typography variant="body2" sx={{ 
                  color: '#6c757d',
                  fontStyle: 'italic',
                  ml: 1,
                }}>
                  Excellence - Automation, predictive capabilities, and self-healing systems
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {Object.entries(app.runMetrics).map(([key, metric]) => (
                  <Grid item xs={12} md={6} key={key}>
                    <Box sx={{ 
                      p: 2, 
                      border: '1px solid #e1e5e9', 
                      borderRadius: '6px', 
                      backgroundColor: metric.implementationStatus === 'No' ? '#ffebee' : 
                                       metric.implementationStatus === 'NA' ? '#e3f2fd' : '#fafafa', 
                      height: '100%' 
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => toggleMetricCompletion(app.appId, 'run', key)}
                            sx={{ p: 0.3 }}
                          >
                            {metric.implementationStatus === 'NA' ? (
                              <Help sx={{ color: '#2196f3', fontSize: 18 }} />
                            ) : metric.completed ? (
                              <CheckCircle sx={{ color: '#27ae60', fontSize: 18 }} />
                            ) : (
                              <Cancel sx={{ color: '#e74c3c', fontSize: 18 }} />
                            )}
                          </IconButton>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '14px' }}>{metric.name}</Typography>
                          {(metric.definition || metric.implementationGuidance || metric.successCriteria) && (
                            <Tooltip
                              title={
                                <Box sx={{ maxWidth: 400 }}>
                                  {metric.definition && (
                                    <Box sx={{ mb: 2 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#fff', mb: 1 }}>
                                        Definition:
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: '#fff', lineHeight: 1.4 }}>
                                        {metric.definition}
                                      </Typography>
                                    </Box>
                                  )}
                                  {metric.implementationGuidance && (
                                    <Box sx={{ mb: 2 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#fff', mb: 1 }}>
                                        Implementation Guidance:
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: '#fff', lineHeight: 1.4 }}>
                                        {metric.implementationGuidance}
                                      </Typography>
                                    </Box>
                                  )}
                                  {metric.successCriteria && (
                                    <Box>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#fff', mb: 1 }}>
                                        Success Criteria:
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: '#fff', lineHeight: 1.4 }}>
                                        {metric.successCriteria}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              }
                              arrow
                              placement="top"
                              sx={{
                                '& .MuiTooltip-tooltip': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                  maxWidth: 'none',
                                  fontSize: '12px',
                                },
                                '& .MuiTooltip-arrow': {
                                  color: 'rgba(0, 0, 0, 0.9)',
                                },
                              }}
                            >
                              <Help sx={{ fontSize: 16, color: '#0067b3', cursor: 'pointer' }} />
                            </Tooltip>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip 
                            size="small" 
                            label={metric.implementationStatus || 'No'}
                            sx={{ 
                              backgroundColor: metric.implementationStatus === 'Yes' ? '#27ae60 !important' : 
                                               metric.implementationStatus === 'NA' ? '#6c757d !important' : '#e74c3c !important',
                              color: 'white !important',
                              '& .MuiChip-label': {
                                color: 'white',
                                fontSize: '11px',
                                fontWeight: 600,
                              }
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => openEditModal(app.appId, 'run', key, metric)}
                            sx={{ p: 0.5 }}
                          >
                            <EditIcon sx={{ fontSize: 16, color: '#0067b3' }} />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      
                      {/* Remarks */}
                      {metric.remarks && (
                        <Box sx={{ display: 'flex', alignItems: 'start', gap: 1, mb: 2 }}>
                          <CommentIcon sx={{ fontSize: 14, color: '#0067b3', mt: 0.2 }} />
                          <Typography variant="body2" sx={{ color: '#2c3e50', fontSize: '13px' }}>
                            {metric.remarks}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Links */}
                      {metric.links && metric.links.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'start', gap: 1 }}>
                          <OpenInNew sx={{ fontSize: 14, color: '#0067b3', mt: 0.2 }} />
                          <Box>
                            {metric.links.map((link, index) => (
                              <Typography 
                                key={index}
                                variant="body2" 
                                component="a"
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ 
                                  color: '#0067b3', 
                                  textDecoration: 'none',
                                  display: 'block',
                                  fontSize: '13px',
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                              >
                                {link.title || link.url}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Paper>
      </Box>
    );
  };

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ backgroundColor: '#ffffff', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2, color: '#0067b3' }} />
          <Typography variant="h6" color="textSecondary">
            Loading Reliability Framework Data...
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Fetching applications and parameters from API
          </Typography>
        </Box>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ backgroundColor: '#ffffff', minHeight: '100vh', p: 4 }}>
        <Alert severity="error" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
          <Typography variant="h6" gutterBottom>
            Error Loading Data
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {error}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
            Please ensure the API server is running at {API_BASE_URL} and the endpoints are accessible.
          </Typography>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />}
            onClick={loadApplicationsData}
            size="small"
            sx={{ mt: 2 }}
          >
            Retry
          </Button>
        </Alert>
      </Box>
    );
  }

  // Show no data state
  if (applications.length === 0) {
    return (
      <Box sx={{ backgroundColor: '#ffffff', minHeight: '100vh', p: 4 }}>
        <Alert severity="info" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
          <Typography variant="h6" gutterBottom>
            No Applications Found
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            No applications were found in the reliability framework. Please check the API configuration.
          </Typography>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />}
            onClick={loadApplicationsData}
            size="small"
            sx={{ mt: 2 }}
          >
            Refresh
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
      <Alert
        severity="warning"
        icon={<Warning sx={{ fontSize: 30 }} />}
        sx={{
          mx: 2,
          mt: 2,
          mb: 2,
          border: '1px solid #f59e0b',
          borderLeft: '6px solid #d97706',
          borderRadius: 1,
          backgroundColor: '#fff8e1',
          boxShadow: '0 2px 8px rgba(180, 83, 9, 0.12)',
          '& .MuiAlert-icon': { color: '#b45309', alignItems: 'flex-start', mt: 0.25 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, width: '100%' }}>
          <Box>
            <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 700, color: '#7c2d12', mb: 0.5 }}>
              Dashboard Migration Notice
            </Typography>
            <Typography variant="body1" sx={{ color: '#7c2d12', lineHeight: 1.6 }}>
              As part of our transition to MTS, this dashboard will be decommissioned from Operational Excellence on <strong>15th October</strong>. This link will redirect automatically to MTS after that date.
            </Typography>
            <Typography variant="body2" sx={{ color: '#7c2d12', mt: 0.5 }}>
              Need help? Visit the <strong>MTS Support Channel</strong>.
            </Typography>
          </Box>
          <Button component="a" href="https://mts.maersk.io/bowler/dr" target="_blank" rel="noopener noreferrer" variant="contained" sx={{ flexShrink: 0, bgcolor: '#b45309', '&:hover': { bgcolor: '#92400e' } }}>
            Open MTS
          </Button>
        </Box>
      </Alert>
      {/* Horizontal Filters */}
      {!filtersCollapsed && (
        <Paper sx={{ 
          mx: 2,
          mt: 2,
          mb: 2,
          p: 2,
          backgroundColor: '#f8f9fa',
          border: '1px solid #e1e5e9',
          borderRadius: '8px',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterList sx={{ color: '#2c3e50', fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                  Filters
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary" sx={{ fontSize: '11px', mt: 0.5 }}>
                Data Source: <a 
                  href="https://teamsite.maerskgroup.com/:x:/r/teams/OperationalExcellence/_layouts/15/doc2.aspx?sourcedoc=%7BA9AA0756-E3FD-4FF4-B05C-A41CBB22E410%7D&file=Reliability%20Framework_2025.xlsm&action=default&mobileredirect=true" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#0067b3', textDecoration: 'underline' }}
                >
                  Reliability Framework
                </a> | Last Refresh: Daily at 04:30 AM UTC
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* AI Insights Button */}
              <Tooltip title="Coming soon" arrow>
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled
                    sx={{ 
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '12px',
                    }}
                  >
                    AI Insights
                  </Button>
                </span>
              </Tooltip>

              {/* View Mode Toggle */}
              <FormControlLabel
                control={
                  <Switch
                    checked={viewMode === 'application'}
                    onChange={(event) => {
                      const newMode = event.target.checked ? 'application' : 'portfolio';
                      setViewMode(newMode);
                      if (newMode === 'application' && applications.length > 0) {
                        const firstApp = filteredApplications[0] || applications[0];
                        setSelectedApp(firstApp.appId);
                        setSelectedApplications([firstApp.appId]);
                      } else {
                        setSelectedApplications([]);
                      }
                    }}
                    size="small"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#00A8CC',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#00A8CC',
                      },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {viewMode === 'portfolio' ? <Dashboard sx={{ fontSize: 18 }} /> : <Assessment sx={{ fontSize: 18 }} />}
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '13px' }}>
                      {viewMode === 'portfolio' ? 'Portfolio Overview' : 'Application Details'}
                    </Typography>
                  </Box>
                }
                sx={{ margin: 0 }}
              />
            </Box>
          </Box>

          {/* Filter Controls in Grid */}
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '13px' }}>Platform Portfolio</InputLabel>
                <Select
                  value={selectedPortfolio}
                  label="Platform Portfolio"
                  onChange={(e) => {
                    const newPortfolio = e.target.value;
                    setSelectedPortfolio(newPortfolio);
                    
                    if (newPortfolio === 'all') {
                      // When "All" portfolios is selected, reset to all platforms (empty = all)
                      setSelectedPlatforms([]);
                    } else if (selectedPlatforms.length > 0) {
                      // Only keep platforms that are valid for the new portfolio
                      const newPlatforms = Array.from(new Set(
                        applications
                          .filter(app => app.portfolio === newPortfolio)
                          .map(app => app.platform)
                      ));
                      const validPlatforms = selectedPlatforms.filter(p => newPlatforms.includes(p));
                      setSelectedPlatforms(validPlatforms);
                    }
                    
                    setSelectedApplications([]);
                    setSelectedGroup('all');
                  }}
                  sx={{ 
                    backgroundColor: '#ffffff',
                    fontSize: '13px',
                    '& .MuiSelect-select': { py: 1 }
                  }}
                >
                  <MenuItem value="all" sx={{ fontSize: '13px' }}>All</MenuItem>
                  {portfolios.map(portfolio => (
                    <MenuItem key={portfolio} value={portfolio} sx={{ fontSize: '13px' }}>{portfolio}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '13px' }}>Platform</InputLabel>
                <Select
                  multiple
                  value={selectedPlatforms}
                  label="Platform"
                  input={<OutlinedInput label="Platform" />}
                  onChange={(e) => {
                    const value = e.target.value as string[];
                    setSelectedPlatforms(value);
                    setSelectedApplications([]);
                    setSelectedGroup('all');
                  }}
                  renderValue={(selected) => {
                    if (selected.length === 0) return <span style={{ color: '#999', fontSize: '13px' }}>All</span>;
                    if (selected.length === 1) return <span style={{ fontSize: '13px' }}>{selected[0]}</span>;
                    return <span style={{ fontSize: '13px' }}>{selected.length} platforms</span>;
                  }}
                  sx={{ 
                    backgroundColor: '#ffffff',
                    fontSize: '13px',
                    '& .MuiSelect-select': { py: 1 }
                  }}
                >
                  {platforms.map(platform => (
                    <MenuItem key={platform} value={platform} sx={{ fontSize: '13px', px: 1 }}>
                      <Checkbox checked={selectedPlatforms.includes(platform)} size="small" sx={{ py: 0.5, px: 1 }} />
                      <ListItemText primary={platform} primaryTypographyProps={{ fontSize: '13px' }} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '13px' }}>Application</InputLabel>
                <Select
                  multiple
                  value={selectedApplications}
                  label="Application"
                  input={<OutlinedInput label="Application" />}
                  onChange={(e) => {
                    const value = e.target.value as string[];
                    setSelectedApplications(value);
                    setSelectedGroup('all');
                    if (value.length === 1) {
                      setSelectedApp(value[0]);
                      setViewMode('application');
                    } else if (value.length === 0 && viewMode === 'application') {
                      setViewMode('portfolio');
                    }
                  }}
                  renderValue={(selected) => {
                    if (selected.length === 0) return <span style={{ color: '#999', fontSize: '13px' }}>All</span>;
                    if (selected.length === 1) {
                      const app = availableApplications.find(a => a.appId === selected[0]);
                      return <span style={{ fontSize: '13px' }}>{app ? app.name : selected[0]}</span>;
                    }
                    return <span style={{ fontSize: '13px' }}>{selected.length} applications</span>;
                  }}
                  sx={{ 
                    backgroundColor: '#ffffff',
                    fontSize: '13px',
                    '& .MuiSelect-select': { py: 1 }
                  }}
                >
                  {availableApplications.map(app => (
                    <MenuItem key={app.appId} value={app.appId} sx={{ fontSize: '13px', px: 1 }}>
                      <Checkbox checked={selectedApplications.includes(app.appId)} size="small" sx={{ py: 0.5, px: 1 }} />
                      <ListItemText primary={app.name} primaryTypographyProps={{ fontSize: '13px' }} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* App ID Search */}
            <Grid item xs={12} sm={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: '100%' }}>
                <TextField
                  size="small"
                  placeholder="Search by App ID or Name"
                  value={appIdSearch}
                  onChange={(e) => setAppIdSearch(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAppIdSearch();
                    }
                  }}
                  sx={{ 
                    flex: 1,
                    '& .MuiInputBase-root': {
                      backgroundColor: '#ffffff',
                      fontSize: '13px',
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button 
                  variant="contained" 
                  size="small"
                  onClick={handleAppIdSearch}
                  disabled={!appIdSearch.trim()}
                  sx={{ 
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '12px',
                    minWidth: 70,
                  }}
                >
                  Search
                </Button>
                {appIdSearchActive && (
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={handleClearAppIdSearch}
                    sx={{ 
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '12px',
                      minWidth: 60,
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Main Content */}
      <Box sx={{ px: 2, pb: 2 }}>
        {viewMode === 'portfolio' 
          ? renderPortfolioOverview() 
          : renderApplicationView()
        }
      </Box>

      {/* Edit Modal */}
      <Dialog 
        open={editModalOpen} 
        onClose={editSaving ? undefined : closeEditModal} 
        maxWidth="md" 
        fullWidth
        disableEscapeKeyDown={editSaving}
      >
        <DialogTitle>
          Edit: {editingMetric?.metric.name}
        </DialogTitle>
        <DialogContent>
          {/* Definition, Implementation Guidance, and Success Criteria Display */}
          {(editingMetric?.metric.definition || editingMetric?.metric.implementationGuidance || editingMetric?.metric.successCriteria) && (
            <Box sx={{ mb: 3 }}>
              {editingMetric?.metric.definition && (
                <Box sx={{ mb: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Definition:
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {editingMetric.metric.definition}
                  </Typography>
                </Box>
              )}
              
              {editingMetric?.metric.implementationGuidance && (
                <Box sx={{ mb: 2, p: 2, backgroundColor: '#e8f4fd', borderRadius: '8px' }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Implementation Guidance:
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {editingMetric.metric.implementationGuidance}
                  </Typography>
                </Box>
              )}
              
              {editingMetric?.metric.successCriteria && (
                <Box sx={{ mb: 2, p: 2, backgroundColor: '#d1ecf1', borderRadius: '8px' }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Success Criteria:
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {editingMetric.metric.successCriteria}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Implementation Status */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Implementation Status *
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Select Status</InputLabel>
              <Select
                value={editImplementationStatus}
                label="Select Status"
                onChange={(e) => setEditImplementationStatus(e.target.value as 'Yes' | 'No' | 'NA')}
              >
                <MenuItem value="Yes">Yes - Implemented</MenuItem>
                <MenuItem value="No">No - Not Implemented</MenuItem>
                <MenuItem value="NA">NA - Not Applicable</MenuItem>
              </Select>
            </FormControl>
            {editImplementationStatus === 'NA' && (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                * Remarks are mandatory when status is "Not Applicable"
              </Typography>
            )}
          </Box>

          {/* Completion Date */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Completion Date
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="date"
              value={editCompletionDate}
              onChange={(e) => setEditCompletionDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Date when this parameter was completed (optional)"
            />
          </Box>

          {/* Remarks */}
          <TextField
            fullWidth
            label={editImplementationStatus === 'NA' ? 'Remarks *' : 'Remarks'}
            multiline
            rows={3}
            value={editRemarks}
            onChange={(e) => setEditRemarks(e.target.value)}
            placeholder={editImplementationStatus === 'NA' ? 'Please explain why this metric is not applicable...' : 'Add remarks, notes, or context...'}
            sx={{ mb: 3 }}
            required={editImplementationStatus === 'NA'}
            error={editImplementationStatus === 'NA' && !editRemarks.trim()}
            helperText={editImplementationStatus === 'NA' && !editRemarks.trim() ? 'This field is required when status is NA' : ''}
          />

          {/* Links Section */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2">Links</Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={addLink}
                sx={{ textTransform: 'none' }}
              >
                Add Link
              </Button>
            </Box>

            {editLinks.length === 0 ? (
              <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', p: 2, border: '1px dashed #e1e5e9', borderRadius: '8px', textAlign: 'center' }}>
                No links added yet. Click "Add Link" to add documentation, dashboards, or relevant resources.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {editLinks.map((link, index) => (
                  <Box key={index} sx={{ p: 2, border: '1px solid #e1e5e9', borderRadius: '8px', backgroundColor: '#fafafa' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body2" fontWeight="medium">Link {index + 1}</Typography>
                      <IconButton
                        size="small"
                        onClick={() => removeLink(index)}
                        sx={{ color: '#e74c3c' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        size="small"
                        label="Link Title"
                        value={link.title}
                        onChange={(e) => updateLink(index, 'title', e.target.value)}
                        placeholder="e.g., 'Azure Monitor Dashboard'"
                      />
                      <TextField
                        size="small"
                        label="URL"
                        value={link.url}
                        onChange={(e) => updateLink(index, 'url', e.target.value)}
                        placeholder="https://example.com/documentation"
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', p: 3 }}>
          {/* Left side - Audit History button */}
          <Button 
            onClick={handleAuditHistoryClick}
            variant="outlined"
            color="secondary"
            startIcon={<HistoryIcon />}
            sx={{ textTransform: 'none' }}
            disabled={editSaving}
          >
            Audit History
          </Button>
          
          {/* Right side - Cancel and Save buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={closeEditModal} sx={{ textTransform: 'none' }} disabled={editSaving}>
              Cancel
            </Button>
            <Button 
              onClick={saveMetricEdit} 
              variant="contained" 
              sx={{ textTransform: 'none' }}
              disabled={editSaving}
              startIcon={editSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {editSaving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Audit History Dialog */}
      <Dialog 
        open={showAuditHistory} 
        onClose={() => setShowAuditHistory(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Audit History - {editingMetric?.metric.name}
          </Typography>
          <IconButton onClick={() => setShowAuditHistory(false)} size="small">
            <Cancel />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {loadingAuditHistory ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : auditHistory.length === 0 ? (
            <Alert severity="info">
              No audit history found for this parameter.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="subtitle2" color="textSecondary">
                Showing last 3 changes
              </Typography>
              {auditHistory.map((entry, index) => (
                <Paper key={entry.id} variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        label="Parameter Update" 
                        size="small" 
                        color="primary" 
                        variant="filled"
                        sx={{ fontSize: '0.75rem', height: '20px' }}
                      />
                      <Typography variant="body2" fontWeight="medium">
                        {entry.changed_by}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      {(() => {
                        const date = new Date(entry.changed_at);
                        const options: Intl.DateTimeFormatOptions = {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true,
                          timeZone: 'UTC'
                        };
                        return date.toLocaleString('en-US', options) + ' UTC';
                      })()}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Chip 
                      label={entry.old_value || 'Not Set'} 
                      size="small" 
                      color="error" 
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: '24px' }}
                    />
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>→</Typography>
                    <Chip 
                      label={entry.new_value || 'Not Set'} 
                      size="small" 
                      color="success" 
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: '24px' }}
                    />
                  </Box>
                  
                  {entry.change_description && (
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                      {entry.change_description}
                    </Typography>
                  )}
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAuditHistory(false)} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EnterpriseReliabilityFramework;